import {
  CASH_BALANCE_SUPPORTED_PLAN_YEAR,
  PLAN_LIMITS,
} from './constants.js'
import { calculateAge, formatCurrency, formatDateOnly, parseLocalDate } from './utils.js'
import { evaluateSafeHarbor } from './safeHarbor.js'
import { adpAcpLimit } from './compliance/adpAcp.js'
import { evaluateCoverage } from './compliance/coverage.js'
import { evaluateCrossTesting } from './compliance/crossTesting.js'
import { applyFamilyAttribution } from './compliance/familyAttribution.js'
import { calculateCashBalanceFormulaCredit } from './cashBalanceFormula.js'
import { allocateProfitSharingComponents } from './profitSharingAllocations.js'
import { TAXABLE_WAGE_BASE_BY_YEAR } from './compliance/constants.js'
import { evaluateMinimumParticipation } from './compliance/combinedDbDc.js'
import { evaluateCashBalanceBenefit } from './compliance/cashBalanceBenefits.js'
import {
  compileToEngine,
  isAuthoredPlan,
  referencedClassesForPlan,
  resolveGroupMembership,
} from './planDesign.js'
import { UNCLASSED_CLASS_ID, deriveAllocationClasses } from './allocationClasses.js'

// Plan-year taxable wage base for integrated (§401(l)) components, with the
// same latest-known-year fallback the cross-testing disparity imputation uses.
const taxableWageBaseForYear = (planYear) => TAXABLE_WAGE_BASE_BY_YEAR[planYear]
  ?? TAXABLE_WAGE_BASE_BY_YEAR[Math.max(...Object.keys(TAXABLE_WAGE_BASE_BY_YEAR).map(Number))]

// SECURE 2.0 §125 long-term part-time (LTPT): from the 2025 plan year an
// employee with 500+ hours in each of 2 consecutive 12-month periods (age 21+)
// must be allowed to make elective deferrals even when they miss the plan's
// normal service/hours eligibility. Plans may still exclude LTPT employees from
// employer contributions and nondiscrimination/top-heavy testing, which is how
// this engine models them. The census carries a single plan year of hours, so
// "500+ hours now AND 24+ months of service" stands in for two consecutive
// 500-hour years; the earlier 3-consecutive-year rule (SECURE 1.0, 2024) and
// true multi-year hour history are not modeled.
export const LTPT_MIN_HOURS = 500
export const LTPT_MIN_SERVICE_MONTHS = 24

/**
 * EMPLOYEE FILTERING UTILITIES
 *
 * These functions provide the single source of truth for filtering employees.
 *
 * Key concepts:
 * - "Active" = employment status is not 'Terminated'
 * - "For metrics" = included in charts/stats based on terminationHandling config
 *
 * Default behavior (includeInMetrics: false) excludes terminated employees from
 * metrics while still calculating their contributions individually.
 */

/** Returns true if employee status is not 'Terminated' */
export const isActiveEmployee = (emp) => (emp.status || 'Active') !== 'Terminated'

/** Returns true if employee status is 'Terminated' */
export const isTerminatedEmployee = (emp) => emp.status === 'Terminated'

/**
 * Filter employees for metrics/charts based on terminationHandling config.
 * This is THE function to use when filtering for visualizations or aggregate stats.
 */
export const getEmployeesForMetrics = (employees, terminationHandling = {}) => {
  const { includeInMetrics = false } = terminationHandling
  return includeInMetrics ? employees : employees.filter(isActiveEmployee)
}

function buildTenure(years, months, days) {
  const safeYears = Number.isFinite(years) ? Math.max(0, Math.floor(years)) : 0
  const safeMonths = Number.isFinite(months) ? Math.max(0, Math.floor(months)) : 0
  const safeDays = Number.isFinite(days) ? Math.max(0, Math.floor(days)) : 0

  const extraMonths = Math.floor(safeDays / 30)
  const normalizedDays = safeDays % 30
  const totalWholeMonths = safeYears * 12 + safeMonths + extraMonths
  const normalizedYears = Math.floor(totalWholeMonths / 12)
  const normalizedMonths = totalWholeMonths % 12
  const totalMonths = totalWholeMonths + (normalizedDays / 30)

  const formattedParts = []
  if (normalizedYears > 0) formattedParts.push(`${normalizedYears}y`)
  if (normalizedMonths > 0) formattedParts.push(`${normalizedMonths}m`)
  if (normalizedDays > 0) formattedParts.push(`${normalizedDays}d`)

  return {
    years: normalizedYears,
    months: normalizedMonths,
    days: normalizedDays,
    totalMonths,
    formatted: formattedParts.length > 0 ? formattedParts.join(' ') : '0m',
  }
}

function parseTenureOverride(value) {
  if (value === null || value === undefined) {
    return { provided: false, tenure: null }
  }
  const normalized = String(value).trim().toLowerCase()
  if (!normalized) return { provided: false, tenure: null }

  if (/^\d+(\.\d+)?$/.test(normalized)) {
    const totalMonths = parseFloat(normalized)
    if (!Number.isFinite(totalMonths)) {
      return { provided: true, tenure: null }
    }
    const wholeMonths = Math.floor(totalMonths)
    const days = Math.round((totalMonths - wholeMonths) * 30)
    return { provided: true, tenure: buildTenure(0, wholeMonths, days) }
  }

  let years = 0
  let months = 0
  let days = 0
  const tokenPattern = /(\d+)\s*(y|yr|yrs|year|years|m|mo|mos|month|months|d|day|days)\b/g
  const matches = [...normalized.matchAll(tokenPattern)]
  const unmatched = normalized
    .replace(tokenPattern, '')
    .replace(/[\s,]+/g, '')
  if (matches.length === 0 || unmatched) {
    return { provided: true, tenure: null }
  }

  for (const match of matches) {
    const amount = parseInt(match[1], 10)
    const unit = match[2]
    if (!Number.isFinite(amount)) continue
    if (unit.startsWith('y')) years += amount
    else if (unit.startsWith('m')) months += amount
    else if (unit.startsWith('d')) days += amount
  }

  return { provided: true, tenure: buildTenure(years, months, days) }
}

// Calculate tenure as of a specific date
function calculateTenure(hireDate, valuationDate, tenureOverride) {
  const override = parseTenureOverride(tenureOverride)
  if (override.provided) return override.tenure

  if (!hireDate) return null

  const hire = parseLocalDate(hireDate)
  if (!hire) return null

  // Use provided valuation date
  const endDate = parseLocalDate(valuationDate)
  if (!endDate) return null

  // Calculate difference in months
  const months = (endDate.getFullYear() - hire.getFullYear()) * 12 + (endDate.getMonth() - hire.getMonth())
  const safeMonths = Math.max(0, months)
  const years = Math.floor(safeMonths / 12)
  const remainingMonths = safeMonths % 12

  return buildTenure(years, remainingMonths, 0)
}

// Check if a date is within a plan year
function isWithinPlanYear(dateStr, planYear) {
  const date = parseLocalDate(dateStr)
  if (!date) return false
  return date.getFullYear() === planYear
}

// Get vesting percent based on schedule and service years
function getVestedPercent(serviceYears, schedule) {
  if (!schedule || schedule.type === 'immediate') return 100
  if (serviceYears === null) return null

  if (schedule.type === 'cliff') {
    return serviceYears >= (schedule.years || 0) ? 100 : 0
  }

  if (schedule.type === 'graded' || schedule.type === 'custom') {
    if (!schedule.schedule || !Array.isArray(schedule.schedule)) return 100
    let vestedPercent = 0
    for (const entry of schedule.schedule) {
      if (serviceYears >= entry.years) {
        vestedPercent = entry.percent
      }
    }
    return vestedPercent
  }

  return 100
}

function getRequirementStatus(value, minimum) {
  if (minimum <= 0) return 'not_applicable'
  if (value === null || value === undefined) return 'unknown'
  return value >= minimum ? 'met' : 'not_met'
}

function getKeyEmployeeStatus(value) {
  const normalized = String(value || '').trim().toUpperCase()
  if (normalized === 'Y') return 'key'
  if (normalized === 'N') return 'non-key'
  return 'unknown'
}

// Calculate tiered match amount
function calculateTieredMatch(deferralPercent, cappedComp, matchTiers) {
  let matchAmount = 0
  let matchBreakdown = []
  let prevCeiling = 0

  for (const tier of matchTiers) {
    const tierCeiling = tier.upToPercent
    const tierMatchRate = tier.matchPercent / 100

    // Calculate how much of employee's deferral falls in this tier
    const employeePercentInTier = Math.max(0, Math.min(deferralPercent, tierCeiling) - prevCeiling)
    const tierMatch = (employeePercentInTier / 100) * cappedComp * tierMatchRate

    if (employeePercentInTier > 0) {
      matchBreakdown.push({
        tierPercent: employeePercentInTier,
        matchRate: tier.matchPercent,
        matchAmount: tierMatch,
      })
    }

    matchAmount += tierMatch
    prevCeiling = tierCeiling
  }

  return { matchAmount, matchBreakdown }
}

function isLeapYear(year) {
  return (year % 4 === 0 && year % 100 !== 0) || (year % 400 === 0)
}

// Calculate fraction of plan year the employee was employed (for proration)
function getPlanYearFraction(hireDate, terminationDate, planYear) {
  const yearStart = new Date(planYear, 0, 1)
  const yearEnd = new Date(planYear, 11, 31)
  const totalDays = isLeapYear(planYear) ? 366 : 365

  let empStart = yearStart
  let empEnd = yearEnd

  if (hireDate) {
    const hire = parseLocalDate(hireDate)
    if (hire && hire > yearStart) empStart = hire
  }

  if (terminationDate) {
    const term = parseLocalDate(terminationDate)
    if (term && term < yearEnd) empEnd = term
  }

  if (empEnd < empStart) return 0
  const daysEmployed = Math.round((empEnd - empStart) / (1000 * 60 * 60 * 24)) + 1
  return Math.min(1, daysEmployed / totalDays)
}

// ==================== Classification (§2.5b) ====================
// The ONLY owner/HCE/key-employee formula in the application. Extracted so the
// whole-plan orchestration can classify before group resolution while the
// per-employee calculator consumes the exact same result (passed down via
// calculationContext.classification; recomputed identically when absent).
//
// Ownership: a more-than-5% owner is, by statute, BOTH a highly compensated
// employee (IRC §414(q)(1)(A)) and a key employee (IRC §416(i)(1)(B))
// regardless of compensation. Ownership comes from three census signals: the
// explicit "Owner >5%" flag, a direct ownership_percent above 5, or §318
// family attribution (applyFamilyAttribution stamps derived_five_percent_owner
// in a cross-employee pre-pass).
// HCE: prior-year compensation vs the prior-year threshold (lookback rule),
// proxied with current compensation when prior-year pay is not provided.
export function classifyEmployeeForPlan(employee, planYear) {
  const limits = PLAN_LIMITS[planYear]
  const rawCompensation = parseFloat(employee.compensation) || 0
  const ownershipPercentRaw = parseFloat(employee.ownership_percent)
  const ownershipPercent = Number.isFinite(ownershipPercentRaw) ? ownershipPercentRaw : null
  const attributedOwner = employee.derived_five_percent_owner === true
  const isFivePercentOwner = String(employee.five_percent_owner || '').trim().toUpperCase() === 'Y'
    || (ownershipPercent !== null && ownershipPercent > 5)
    || attributedOwner
  const priorYearComp = parseFloat(employee.prior_year_compensation) || 0
  // Ownership makes HCE certain, so it is never an estimate even without prior-year comp.
  const hceIsEstimated = !isFivePercentOwner && priorYearComp <= 0
  const hceComp = priorYearComp > 0 ? priorYearComp : rawCompensation
  // For plan year Y, compare to PLAN_LIMITS[Y-1].hceThreshold (prior-year threshold)
  const priorLimits = PLAN_LIMITS[planYear - 1]
  const hceThreshold = priorLimits ? priorLimits.hceThreshold : limits?.hceThreshold
  const isHCE = isFivePercentOwner || hceComp > hceThreshold
  // A >5% owner is always key; otherwise use the explicit Y/N census value.
  const keyEmployeeStatus = isFivePercentOwner ? 'key' : getKeyEmployeeStatus(employee.key_employee)
  return { isFivePercentOwner, attributedOwner, ownershipPercent, hceIsEstimated, isHCE, keyEmployeeStatus }
}

export function classifyEmployeesForPlan(employees = [], planYear) {
  return new Map((Array.isArray(employees) ? employees : [])
    .map((employee) => [employee.id, classifyEmployeeForPlan(employee, planYear)]))
}

export function calculateEmployeeContributions(
  employee,
  planYear,
  settings,
  calculationContext = {},
) {
  // Engine boundary: an authored (v10) plan compiles here so direct callers
  // (unit tests, insight screens) never invoke the compiler themselves.
  // calculatePlanResults always passes an already-compiled engine plan.
  const effectiveSettings = isAuthoredPlan(settings) ? compileToEngine(settings) : settings
  if (!Array.isArray(effectiveSettings?.employerContributions?.profitSharingAllocations)) {
    throw new Error('calculateEmployeeContributions requires an authored or compiled current plan.')
  }
  const limits = PLAN_LIMITS[planYear]
  if (!limits) {
    console.error(`No limits found for plan year ${planYear}`)
    return null
  }

  const {
    employerContributions,
    eligibility,
    vesting,
    terminationHandling = {},
    afterTax = {},
    prorateCompensation = false,
  } = effectiveSettings

  const cashBalance = effectiveSettings.cashBalance || {}
  const cbEnabled = cashBalance.enabled === true
    && Number(planYear) === CASH_BALANCE_SUPPORTED_PLAN_YEAR
  const cbEligibilitySettings = cashBalance.eligibility || { enabled: false }
  const {
    terminatedEarnContributions = true,
  } = terminationHandling

  const {
    allowed: afterTaxAllowed = false,
    maxPercent: afterTaxMaxPercent = 0,
    maxDollar: afterTaxMaxDollar = 0,
  } = afterTax || {}

  const {
    matchEnabled,
    matchMode = 'simple',
    matchTiers,
    matchGroups = [],
    matchCapPerEmployee = 0,
    necEnabled,
    necPercent,
    necIsSafeHarbor,
    profitSharingEnabled,
    profitSharingConditions = {},
  } = employerContributions

  const {
    enabled: eligibilityEnabled = true,
    minAge: eligibilityMinAge = 21,
    minServiceMonths: eligibilityMinServiceMonths = 12,
    minHours: eligibilityMinHours = 1000,
  } = eligibility || {}
  const ltptDeferralsRequired = Number(planYear) >= 2025

  const {
    enabled: vestingEnabled = false,
    matchSchedule = { type: 'immediate' },
    necSchedule = { type: 'immediate' },
    profitSharingSchedule = { type: 'immediate' },
  } = vesting || {}

  // ==================== WARNINGS ====================
  // `warnings` holds every note shown in the per-employee detail panel.
  // `reviewWarnings` is the subset that means the data is missing/invalid/
  // contradictory so results are uncertain – these drive the "records require
  // review" banner. Advisory notes (valid input handled deterministically per
  // plan rules, e.g. after-tax ignored) are `review: false` and stay out of it.
  const warnings = []
  const reviewWarnings = []
  const advisoryWarnings = []
  const addWarning = (message, { review = true } = {}) => {
    warnings.push(message)
    if (review) reviewWarnings.push(message)
    else advisoryWarnings.push(message)
  }

  // ==================== STEP 1: Determine valuation date ====================
  const rawStatus = employee.status || 'Active'
  const employmentStatus = rawStatus
  const planYearEnd = new Date(planYear, 11, 31)
  const hireDateInput = employee.hire_date
  const hireDate = parseLocalDate(hireDateInput)
  const hasHireDateInput = Boolean(
    hireDateInput && String(hireDateInput).trim()
  )
  const terminationDateInput = employee.termination_date
  const parsedTerminationDate = parseLocalDate(terminationDateInput)
  const hasTerminationDateInput = Boolean(
    terminationDateInput && String(terminationDateInput).trim()
  )

  if (hasHireDateInput && !hireDate) {
    addWarning('Hire date is invalid and was ignored.')
  }

  const isTerminated = employmentStatus === 'Terminated'
  let effectiveTerminationDate = null

  if (!isTerminated && hasTerminationDateInput) {
    addWarning(`Termination date is ignored because status is ${rawStatus}.`, { review: false })
  } else if (isTerminated) {
    if (!hasTerminationDateInput) {
      addWarning(`Termination date is missing; ${planYear} plan-year end was used for age, service, and vesting.`)
    } else if (!parsedTerminationDate) {
      addWarning(`Termination date is invalid; ${planYear} plan-year end was used for age, service, and vesting.`)
    } else if (hireDate && parsedTerminationDate < hireDate) {
      addWarning(`Termination date precedes the hire date; ${planYear} plan-year end was used for age, service, and vesting.`)
    } else if (parsedTerminationDate > planYearEnd) {
      addWarning(`Termination date is after the ${planYear} plan year; ${planYear} plan-year end was used for age, service, and vesting.`, { review: false })
    } else {
      effectiveTerminationDate = parsedTerminationDate
    }
  }

  const termDateInYear = effectiveTerminationDate?.getFullYear() === planYear

  // Determine valuation date for age/service/vesting calculations
  let valuationDate
  if (isTerminated && effectiveTerminationDate) {
    valuationDate = effectiveTerminationDate
  } else {
    valuationDate = planYearEnd
  }

  // ==================== STEP 2: Compute employee facts ====================
  const age = calculateAge(employee.dob, valuationDate)
  const rawCompensation = parseFloat(employee.compensation) || 0

  // Proration: if enabled and hire/term dates bound the plan year, scale compensation
  let compensation = rawCompensation
  let compWasProrated = false
  if (prorateCompensation && rawCompensation > 0) {
    const hiresThisYear = isWithinPlanYear(hireDateInput, planYear)
    const terminatesThisYear = termDateInYear
    if (hiresThisYear || terminatesThisYear) {
      const fraction = getPlanYearFraction(hireDate, effectiveTerminationDate, planYear)
      if (fraction < 0.999) {
        compensation = rawCompensation * fraction
        compWasProrated = true
      }
    }
  }

  const cappedCompensation = Math.min(compensation, limits.compCap401a17)
  const max415c = limits.planLimit415c

  const explicitCbEntryDate = parseLocalDate(employee.cb_participant_entry_date)
  const resolvedCbEntryDate = explicitCbEntryDate
  const explicitHigh3 = employee.high_3_compensation === '' || employee.high_3_compensation == null
    ? null : Number(employee.high_3_compensation)
  const explicit415Comp = employee.section_415_compensation === '' || employee.section_415_compensation == null
    ? null : Number(employee.section_415_compensation)
  const resolvedHigh3 = Number.isFinite(explicitHigh3) ? explicitHigh3 : null
  const resolved415Comp = Number.isFinite(explicit415Comp) ? explicit415Comp : null

  // §415(c)(3) compensation for the minimum-allocation gateway: the gateway is
  // measured on gross 415 comp, NOT the plan's (possibly narrower) definition.
  // Explicit census 415 comp wins; otherwise raw compensation is the proxy.
  const gatewayCompensation = Math.min(
    Number.isFinite(explicit415Comp) && explicit415Comp > 0 ? explicit415Comp : rawCompensation,
    limits.compCap401a17,
  )

  // Reallocated forfeitures count as annual additions in the general test and
  // toward the gateway (1.401(a)(4)-2(c)(2)(ii)); optional census input.
  const forfeitureAllocationRaw = parseFloat(employee.forfeiture_allocation)
  const forfeitureAllocation = Number.isFinite(forfeitureAllocationRaw)
    ? Math.max(0, forfeitureAllocationRaw) : 0

  // QNECs are 100%-vested employer nonelective dollars. Like forfeitures they are
  // a fixed annual addition (they consume §415(c) headroom before discretionary
  // match/NEC/PS) but, unlike forfeitures, they are new employer cost. They count
  // in the general test and the gateway as nonelective allocation.
  const qnecAmountRaw = parseFloat(employee.qnec_amount)
  const qnecAmount = Number.isFinite(qnecAmountRaw) ? Math.max(0, qnecAmountRaw) : 0

  const hoursRequiredForEligibility = (eligibilityEnabled && eligibilityMinHours > 0)
    || (cbEnabled && cbEligibilitySettings.enabled !== false && Number(cbEligibilitySettings.minHours) > 0)
  const serviceRequiredForEligibility = (eligibilityEnabled && eligibilityMinServiceMonths > 0)
    || (cbEnabled && cbEligibilitySettings.enabled !== false && Number(cbEligibilitySettings.minServiceMonths) > 0)
  const serviceRequiredForVesting = vestingEnabled && (
    (matchEnabled && matchSchedule?.type !== 'immediate')
    || (necEnabled && !necIsSafeHarbor && necSchedule?.type !== 'immediate')
    || (profitSharingEnabled && profitSharingSchedule?.type !== 'immediate')
  )

  const hasHoursValue = employee.hours_served !== null
    && employee.hours_served !== undefined
    && String(employee.hours_served).trim() !== ''
  const normalizedHours = String(employee.hours_served ?? '').trim()
  const parsedHours = Number(normalizedHours)
  const hoursServed = hasHoursValue
    && /^\d+(\.\d+)?$/.test(normalizedHours)
    && Number.isFinite(parsedHours)
    ? parsedHours
    : null
  if (hoursRequiredForEligibility && hasHoursValue && hoursServed === null) {
    addWarning('Hours are invalid; hours-based eligibility is unknown.')
  }

  // Calculate tenure as of valuation date
  const tenureOverride = parseTenureOverride(employee.tenure)
  const tenure = calculateTenure(hireDate, valuationDate, employee.tenure)
  const serviceUses = [
    serviceRequiredForEligibility ? 'eligibility' : null,
    serviceRequiredForVesting ? 'vesting' : null,
  ].filter(Boolean)
  if (!tenure && serviceUses.length > 0) {
    const affectedCalculations = serviceUses.join(' and ')
    if (tenureOverride.provided) {
      addWarning(`Tenure override is invalid; service-based ${affectedCalculations} is unknown.`)
    } else if (!hireDate) {
      addWarning(`Hire date and tenure are blank or invalid; service-based ${affectedCalculations} is unknown.`)
    }
  }

  // ==================== Classification ====================
  // Owner/HCE/key status comes from the single extracted formula in
  // classifyEmployeeForPlan — precomputed by the whole-plan orchestration and
  // passed down, or derived identically here for direct callers. Warnings
  // about the classification are emitted only in this calculator.
  const {
    isFivePercentOwner,
    attributedOwner,
    ownershipPercent,
    hceIsEstimated,
    isHCE,
    keyEmployeeStatus,
  } = calculationContext.classification ?? classifyEmployeeForPlan(employee, planYear)
  if (attributedOwner) {
    addWarning(
      `Treated as a more-than-5% owner via §318 family attribution from ${employee.attributed_ownership_from}. Attribution covers the owner's spouse, children, parents, and grandparents — a grandchild or sibling of the owner is NOT attributed; clear Family Of if this relationship does not apply.`,
      { review: false },
    )
  }
  if (employee.family_attribution_unmatched) {
    addWarning(
      `Family Of references "${employee.family_of}", which does not match any more-than-5% owner by name or ID, so no ownership was attributed.`,
    )
  }

  const allocationGroupLabel = String(employee.allocation_group || '').trim()

  // §416(i) officer-threshold advisory: an officer earning above the key-employee
  // compensation threshold is a key employee regardless of the census flag. We
  // can't tell whether someone is an officer, so when a record is explicitly
  // marked non-key, is not a >5% owner, and out-earns the threshold, flag it for
  // review rather than silently trusting the flag. Independent of cash balance.
  if (
    limits.keyEmployeeThreshold
    && getKeyEmployeeStatus(employee.key_employee) === 'non-key'
    && !isFivePercentOwner
    && rawCompensation > limits.keyEmployeeThreshold
  ) {
    addWarning(
      `Compensation exceeds the §416 officer threshold (${formatCurrency(limits.keyEmployeeThreshold)}) but the employee is marked non-key — confirm they are not an officer.`,
      { review: false },
    )
  }

  // ==================== STEP 3: Determine plan eligibility ====================
  // Eligibility gates the whole plan, the common plan design: an employee who
  // fails the age/service/hours requirements cannot make employee deferrals
  // either – they are simply not a participant yet. The one statutory
  // carve-out is the SECURE 2.0 long-term part-time rule, which lets a
  // qualifying part-timer defer while staying excluded from employer money.
  const requirements = eligibilityEnabled
    ? {
        age: getRequirementStatus(age, eligibilityMinAge),
        service: getRequirementStatus(tenure?.totalMonths ?? null, eligibilityMinServiceMonths),
        hours: getRequirementStatus(hoursServed, eligibilityMinHours),
      }
    : {
        age: 'not_applicable',
        service: 'not_applicable',
        hours: 'not_applicable',
      }

  const eligibilityReasons = []
  const failedRequirements = Object.entries(requirements)
    .filter(([, status]) => status === 'not_met')
    .map(([requirement]) => requirement)
  const unknownRequirements = Object.entries(requirements)
    .filter(([, status]) => status === 'unknown')
    .map(([requirement]) => requirement)

  const fullTimeFlag = String(employee.full_time || '').trim().toUpperCase()
  const terminatedBlocksEmployer = isTerminated && !terminatedEarnContributions

  let eligibilityStatus = 'eligible'
  let allowsEmployerContributions = true
  let allowsDeferrals = true
  let isLtpt = false

  if (terminatedBlocksEmployer) {
    eligibilityStatus = 'ineligible'
    allowsEmployerContributions = false
    eligibilityReasons.push('Terminated employees do not earn employer contributions under this plan setting.')
  }

  if (failedRequirements.length > 0) {
    eligibilityStatus = 'ineligible'
    allowsEmployerContributions = false
    allowsDeferrals = false
    eligibilityReasons.push(`Failed plan eligibility requirement${failedRequirements.length === 1 ? '' : 's'}: ${failedRequirements.join(', ')} – not yet a participant, so no employee deferrals or employer contributions.`)

    // LTPT proxy (see LTPT_MIN_* at the top of this file): met the age rule,
    // not marked full-time, 500+ plan-year hours, and 24+ months of service.
    const ltptQualifies = ltptDeferralsRequired
      && !failedRequirements.includes('age')
      && requirements.age !== 'unknown'
      && fullTimeFlag !== 'Y'
      && hoursServed !== null && hoursServed >= LTPT_MIN_HOURS
      && (tenure?.totalMonths ?? 0) >= LTPT_MIN_SERVICE_MONTHS
    if (ltptQualifies) {
      isLtpt = true
      allowsDeferrals = true
      eligibilityStatus = 'ltpt'
      eligibilityReasons.push('Long-term part-time (SECURE 2.0): may make employee deferrals, but is excluded from employer contributions and testing.')
      addWarning(`Modeled as long-term part-time: 500+ hours and 2+ years of service. The statute requires 500+ hours in each of the two most recent years and the census only holds ${planYear} hours – confirm the prior year.`, { review: false })
    }
  } else if (unknownRequirements.length > 0 && !terminatedBlocksEmployer) {
    eligibilityStatus = 'unknown'
    eligibilityReasons.push(`Missing required ${unknownRequirements.join(', ')} data.`)
    addWarning(`Plan eligibility is unknown because ${unknownRequirements.join(', ')} data is missing; the employee was modeled as a full participant (deferrals and employer contributions).`)
  }

  if (eligibilityEnabled && fullTimeFlag === 'Y' && requirements.hours === 'not_met') {
    addWarning(`Marked full-time but ${planYear} hours (${hoursServed}) are below the plan's ${eligibilityMinHours}-hour requirement – check the hours or the full-time flag.`)
  }

  const eligibilityResult = {
    status: eligibilityStatus,
    allowsEmployerContributions,
    allowsDeferrals,
    isLtpt,
    requirements,
    reasons: eligibilityReasons,
  }

  // Cash-balance participation can use a separate document eligibility and
  // entry schedule. It never expands general plan eligibility or LTPT rights;
  // it can only further restrict the defined-benefit credit.
  const cbRequirements = cbEnabled && cbEligibilitySettings.enabled !== false
    ? {
        age: getRequirementStatus(age, Number(cbEligibilitySettings.minAge) || 0),
        service: getRequirementStatus(tenure?.totalMonths ?? null, Number(cbEligibilitySettings.minServiceMonths) || 0),
        hours: getRequirementStatus(hoursServed, Number(cbEligibilitySettings.minHours) || 0),
      }
    : { age: 'not_applicable', service: 'not_applicable', hours: 'not_applicable' }
  const cbFailedRequirements = Object.entries(cbRequirements)
    .filter(([, status]) => status === 'not_met')
    .map(([requirement]) => requirement)
  const cbUnknownRequirements = Object.entries(cbRequirements)
    .filter(([, status]) => status === 'unknown')
    .map(([requirement]) => requirement)
  const allowsCashBalanceCredit = cbEnabled
    && allowsEmployerContributions
    && cbFailedRequirements.length === 0
    && cbUnknownRequirements.length === 0
  if (cbEnabled && cbUnknownRequirements.length > 0) {
    addWarning(`Cash-balance eligibility is unknown because ${cbUnknownRequirements.join(', ')} data is missing; no cash-balance credit was modeled.`)
  }
  const cashBalanceEligibilityResult = {
    status: !cbEnabled ? 'not_applicable'
      : allowsCashBalanceCredit ? 'eligible'
        : cbUnknownRequirements.length > 0 ? 'unknown' : 'ineligible',
    allowsCredit: allowsCashBalanceCredit,
    requirements: cbRequirements,
    failedRequirements: cbFailedRequirements,
    unknownRequirements: cbUnknownRequirements,
    entryFrequency: cbEligibilitySettings.entryFrequency || 'semiannual',
  }

  // ==================== STEP 4: Compute employee deferral requests ====================
  let pretaxPercent = parseFloat(employee.pretax_percent) || 0
  let rothPercent = parseFloat(employee.roth_percent) || 0
  const pretaxAmount = parseFloat(employee.pretax_amount) || 0
  const rothAmount = parseFloat(employee.roth_amount) || 0

  const usingPretaxAmount = pretaxAmount > 0
  const usingRothAmount = rothAmount > 0

  // Calculate desired contributions from inputs
  const desiredPretax = usingPretaxAmount ? pretaxAmount : (pretaxPercent / 100) * cappedCompensation
  const desiredRoth = usingRothAmount ? rothAmount : (rothPercent / 100) * cappedCompensation
  const desiredDeferral = desiredPretax + desiredRoth

  // Ineligible employees are not participants: their requested deferrals are
  // recorded (desiredDeferral) but never modeled as contributions.
  const modeledDeferral = allowsDeferrals ? desiredDeferral : 0
  if (!allowsDeferrals && desiredDeferral > 0) {
    addWarning('Not eligible to participate: requested employee deferrals were not modeled.', { review: false })
  }

  // Calculate effective percentages for display
  const effectivePretaxPercent = allowsDeferrals && cappedCompensation > 0 ? (desiredPretax / cappedCompensation) * 100 : 0
  const effectiveRothPercent = allowsDeferrals && cappedCompensation > 0 ? (desiredRoth / cappedCompensation) * 100 : 0
  const totalDeferralPercent = effectivePretaxPercent + effectiveRothPercent

  // ==================== STEP 5: Apply elective deferral limits ====================
  let catchupLimit = 0
  let catchupType = 'none'

  if (age !== null) {
    if (age >= limits.superCatchupStart && age <= limits.superCatchupEnd) {
      catchupLimit = limits.superCatchup60to63
      catchupType = 'super'
    } else if (age >= limits.catchupAge) {
      catchupLimit = limits.catchup50to59
      catchupType = 'standard'
    }
  }

  // Regular deferral capped at limit
  const regularDeferralCap = limits.deferralLimit
  const regularTotal = Math.min(modeledDeferral, regularDeferralCap)

  // Catch-up is the excess, capped at catch-up limit
  const catchupContribution = Math.min(
    Math.max(0, modeledDeferral - regularDeferralCap),
    catchupLimit
  )

  // Total employee contribution
  const totalEmployeeContribution = regularTotal + catchupContribution

  // Split proportionally between pre-tax and Roth
  const requestedTotal = desiredPretax + desiredRoth
  let actualPretax = 0
  let actualRoth = 0

  if (requestedTotal > 0) {
    const pretaxRatio = desiredPretax / requestedTotal
    actualPretax = totalEmployeeContribution * pretaxRatio
    actualRoth = totalEmployeeContribution * (1 - pretaxRatio)
  }

  // Deferral % for matching purposes (regular deferrals only, not catch-up)
  const matchBaseDeferralPercent = cappedCompensation > 0 ? (regularTotal / cappedCompensation) * 100 : 0

  // ==================== STEP 6: Compute employer contributions (all sources) ====================

  // A) Tiered Match
  let matchAmount = 0
  let matchBreakdown = []
  let matchGroupLabel = null
  let matchGroupMatched = false
  const matchGroupId = calculationContext.resolvedGroupIds?.match ?? null
  const activeMatchGroup = matchMode === 'grouped'
    ? matchGroups.find((group) => group.groupId === matchGroupId) || null
    : null
  const activeMatchTiers = matchMode === 'grouped'
    ? (activeMatchGroup?.matchTiers || [])
    : matchTiers
  if (matchEnabled && allowsEmployerContributions) {
    if (matchMode !== 'grouped' || activeMatchGroup) {
      if (activeMatchGroup) {
        matchGroupMatched = true
        matchGroupLabel = calculationContext.groupLabelsById?.[activeMatchGroup.groupId] || ''
      }
      const matchResult = calculateTieredMatch(matchBaseDeferralPercent, cappedCompensation, activeMatchTiers)
      matchAmount = matchResult.matchAmount
      matchBreakdown = matchResult.matchBreakdown
    }

    // Apply per-employee match cap if set
    if (matchCapPerEmployee > 0 && matchAmount > matchCapPerEmployee) {
      const reductionRatio = matchCapPerEmployee / matchAmount
      matchAmount = matchCapPerEmployee
      matchBreakdown = matchBreakdown.map(tier => ({
        ...tier,
        matchAmount: tier.matchAmount * reductionRatio,
      }))
    }
  }

  // B) NEC (Non-Elective Contribution)
  let necAmount = 0
  if (necEnabled && allowsEmployerContributions) {
    necAmount = (necPercent / 100) * cappedCompensation
  }

  // C) Profit Sharing
  // Allocation conditions gate PS only (not match/NEC): a minimum-hours and/or
  // last-day-of-year employment requirement, the standard plan-document levers
  // that keep short-service and mid-year-terminated employees out of the
  // allocation. Someone failing them is NOT benefiting under the PS component —
  // which matters downstream: they drop out of the minimum-allocation gateway
  // (a coverage question instead) but stay in rate-group denominators.
  const psMinHours = Math.max(0, Number(profitSharingConditions.minHours) || 0)
  const psLastDayRequired = profitSharingConditions.lastDayRequired === true
  // Blank hours never mean zero: an unknown hours value is treated as meeting
  // the hours condition, with a review warning, rather than silently stripping
  // the allocation.
  const psHoursKnown = hoursServed !== null
  const psHoursConditionMet = psMinHours === 0 || !psHoursKnown || hoursServed >= psMinHours
  const profitSharingConditionMet = psHoursConditionMet && (!psLastDayRequired || !isTerminated)
  if (profitSharingEnabled && allowsEmployerContributions && psMinHours > 0 && !psHoursKnown) {
    addWarning(`Hours are missing, so the ${psMinHours}-hour profit-sharing allocation condition was assumed met — confirm.`)
  }
  if (profitSharingEnabled && allowsEmployerContributions && !profitSharingConditionMet) {
    addWarning(
      `Receives no profit sharing: fails the plan's allocation condition${psMinHours > 0 && psLastDayRequired ? 's' : ''} (${[psMinHours > 0 ? `${psMinHours}-hour minimum` : null, psLastDayRequired ? 'employed on the last day' : null].filter(Boolean).join(' and ')}).`,
      { review: false },
    )
  }
  let profitSharingAmount = 0
  let profitSharingGroupLabel = null
  let profitSharingGroupMatched = false
  let profitSharingLimitedBy415c = false
  const profitSharingOverride = calculationContext.profitSharingOverride
  if (profitSharingEnabled && allowsEmployerContributions && profitSharingConditionMet) {
    if (profitSharingOverride) {
      profitSharingAmount = Math.max(0, Number(profitSharingOverride.amount) || 0)
      profitSharingLimitedBy415c = profitSharingOverride.capLimited === true
      const groupedComponent = (profitSharingOverride.components || [])
        .find((component) => component.groupId)
      if (groupedComponent) {
        profitSharingGroupMatched = true
        profitSharingGroupLabel = groupedComponent.groupLabel || ''
      }
    } else {
      throw new Error('Enabled profit sharing requires the plan-level allocation pass.')
    }
  }

  // D) Cash balance (defined benefit) hypothetical benefit credit — OUTSIDE
  // §415(c) and OUTSIDE DC employer cost. Only explicit plan-document formulas
  // feed the adopted credit; benchmark overlays are comparison-only.
  let cashBalanceCredit = 0
  let cashBalanceGroupLabel = null
  let cashBalanceGroupMatched = false

  if (cbEnabled) {
    // Structured cash balance keys credits by exact group ID through the
    // runtime resolution result.
    const group = (cashBalance.groups || [])
      .find((entry) => entry.groupId === calculationContext.resolvedGroupIds?.cashBalance) || null
    if (group) {
      const formula = group.formula || {}
      const label = String(group.groupLabel || calculationContext.groupLabelsById?.[group.groupId] || '').trim()
      const requestedCredit = calculateCashBalanceFormulaCredit(formula, cappedCompensation, {
        serviceYears: tenure ? Math.floor(tenure.totalMonths / 12) : null,
        taxableWageBase: calculationContext.taxableWageBase ?? taxableWageBaseForYear(planYear),
      })
      const wouldCredit = requestedCredit > 0

      if (!allowsCashBalanceCredit) {
        if (wouldCredit) {
          addWarning('Not eligible: cash balance credit was not modeled.', { review: false })
        }
      } else {
        cashBalanceGroupMatched = true
        cashBalanceGroupLabel = label

        cashBalanceCredit = Math.max(0, requestedCredit)
      }
    } else if (allowsCashBalanceCredit) {
      // The employee's allocation class carries no cash-balance formula, so
      // they get nothing. A class never borrows another class's credit, and a
      // silent $0 here is how an owner ends up out of their own pension plan,
      // so it is surfaced for review rather than left to the reader to notice.
      const className = String(calculationContext.groupLabelsById?.[calculationContext.resolvedGroupIds?.cashBalance] || '').trim()
      addWarning(className
        ? `No cash balance credit is defined for allocation class "${className}", so this employee receives none.`
        : 'No cash balance credit is defined for this employee\'s allocation class, so they receive none.')
    }
  }

  // E) Employer total before 415(c) cap (cash balance intentionally excluded)
  let employerTotalPreCap = matchAmount + necAmount + profitSharingAmount

  // ==================== STEP 7: Apply 415(c) annual additions cap ====================
  // Reallocated forfeitures are annual additions under §415(c)(2) and are a fixed
  // prior allocation (a plan fact, not a discretionary lever), so — like elective
  // deferrals — they consume 415(c) headroom before match/NEC/PS are fit in.
  const priorAdditions = regularTotal + forfeitureAllocation + qnecAmount
  const annualAdditionsPreCap = priorAdditions + employerTotalPreCap
  let exceeds415c = false

  if (annualAdditionsPreCap > max415c) {
    exceeds415c = true
    const employerAllowed = Math.max(0, max415c - priorAdditions)
    let excess = employerTotalPreCap - employerAllowed

    // Deterministic reduction order: Profit Sharing → Match → NEC (safe harbor last)
    if (excess > 0 && profitSharingAmount > 0) {
      const psReduction = Math.min(profitSharingAmount, excess)
      profitSharingAmount -= psReduction
      excess -= psReduction
      if (psReduction > 0) profitSharingLimitedBy415c = true
    }

    if (excess > 0 && matchAmount > 0) {
      const matchReduction = Math.min(matchAmount, excess)
      matchAmount -= matchReduction
      excess -= matchReduction
      if (matchReduction > 0 && matchBreakdown.length > 0) {
        const reductionRatio = matchAmount / (matchAmount + matchReduction)
        matchBreakdown = matchBreakdown.map(tier => ({
          ...tier,
          matchAmount: tier.matchAmount * reductionRatio,
        }))
      }
    }

    if (excess > 0 && necAmount > 0) {
      const necReduction = Math.min(necAmount, excess)
      necAmount -= necReduction
    }
  }

  const employerTotal = matchAmount + necAmount + profitSharingAmount

  // ==================== STEP 7b: After-tax (mega-backdoor Roth) contributions ====================
  // After-tax dollars count toward the 415(c) annual additions cap but not the elective
  // deferral limit. They fill the remaining 415(c) headroom after elective deferrals and
  // employer contributions, so they never push annual additions past the cap.
  const afterTaxPercentInput = parseFloat(employee.after_tax_percent) || 0
  const afterTaxAmountInput = parseFloat(employee.after_tax_amount) || 0
  const usingAfterTaxAmount = afterTaxAmountInput > 0
  const requestedAfterTax = usingAfterTaxAmount
    ? afterTaxAmountInput
    : (afterTaxPercentInput / 100) * cappedCompensation

  let afterTaxContribution = 0
  let afterTaxLimitedBy415c = false

  if (requestedAfterTax > 0 && !allowsDeferrals) {
    addWarning('Not eligible to participate: requested after-tax contributions were not modeled.', { review: false })
  } else if (requestedAfterTax > 0 && !afterTaxAllowed) {
    addWarning('After-tax contributions were ignored because the plan does not allow after-tax contributions.', { review: false })
  } else if (requestedAfterTax > 0) {
    // Plan-level caps (0 means no plan-specific cap; 415(c) still applies)
    const planCapByPercent = afterTaxMaxPercent > 0
      ? (afterTaxMaxPercent / 100) * cappedCompensation
      : Infinity
    const planCapByDollar = afterTaxMaxDollar > 0 ? afterTaxMaxDollar : Infinity
    const planCappedAfterTax = Math.min(requestedAfterTax, planCapByPercent, planCapByDollar)
    if (planCappedAfterTax < requestedAfterTax) {
      addWarning('After-tax contribution was reduced to the plan\'s after-tax limit.', { review: false })
    }

    // Fill remaining 415(c) headroom after deferrals, forfeitures, QNECs, and employer contributions.
    const afterTaxHeadroom = Math.max(0, max415c - regularTotal - forfeitureAllocation - qnecAmount - employerTotal)
    afterTaxContribution = Math.min(planCappedAfterTax, afterTaxHeadroom)
    if (afterTaxContribution < planCappedAfterTax) {
      afterTaxLimitedBy415c = true
      addWarning('After-tax contribution was reduced to stay within the 415(c) annual additions limit.', { review: false })
    }
  }

  // The maximum after-tax this employee *could* contribute – the plan cap capped
  // by remaining 415(c) headroom – regardless of what they actually elected. The
  // mega-backdoor Roth ACP estimator uses this to model "every HCE maxes after-tax".
  let afterTaxCapacity = 0
  if (afterTaxAllowed && allowsDeferrals) {
    const capByPercent = afterTaxMaxPercent > 0 ? (afterTaxMaxPercent / 100) * cappedCompensation : Infinity
    const capByDollar = afterTaxMaxDollar > 0 ? afterTaxMaxDollar : Infinity
    const headroom = Math.max(0, max415c - regularTotal - forfeitureAllocation - qnecAmount - employerTotal)
    afterTaxCapacity = Math.min(capByPercent, capByDollar, headroom)
  }

  // ==================== STEP 8: Apply vesting ====================
  const serviceYears = tenure ? Math.floor(tenure.totalMonths / 12) : null

  // Statutory "otherwise excludable" status (§1.410(b)-7(c)(3)): an employee who
  // has not met BOTH the statutory maximum age (21) AND one year of service is
  // otherwise excludable and may be disaggregated into a separate testing group,
  // regardless of the plan's (more generous) actual eligibility. Unknown age or
  // service is treated as NOT excludable (conservative — never carve out someone
  // we cannot confirm is statutorily excludable).
  const otherwiseExcludable = (Number.isFinite(age) && age < 21)
    || (tenure?.totalMonths != null && tenure.totalMonths < 12)

  let vestedMatchPercent = 100
  let vestedNecPercent = 100
  let vestedProfitSharingPercent = 100
  let vestedCashBalancePercent = 100

  if (vestingEnabled) {
    vestedMatchPercent = getVestedPercent(serviceYears, matchSchedule)
    vestedNecPercent = necIsSafeHarbor ? 100 : getVestedPercent(serviceYears, necSchedule)
    vestedProfitSharingPercent = getVestedPercent(serviceYears, profitSharingSchedule)
    vestedCashBalancePercent = getVestedPercent(
      serviceYears,
      cashBalance.vestingSchedule || { type: 'immediate' },
    )
  }

  const getVestingAmounts = (amount, percent) => {
    if (percent === null) {
      return { vested: null, unvested: null, unresolved: amount }
    }
    const vested = amount * (percent / 100)
    return { vested, unvested: amount - vested, unresolved: 0 }
  }

  const matchVesting = getVestingAmounts(matchAmount, vestedMatchPercent)
  const necVesting = getVestingAmounts(necAmount, vestedNecPercent)
  const profitSharingVesting = getVestingAmounts(profitSharingAmount, vestedProfitSharingPercent)

  const vestedMatch = matchVesting.vested
  const vestedNec = necVesting.vested
  const vestedProfitSharing = profitSharingVesting.vested
  const knownVestedEmployerTotal = (vestedMatch || 0) + (vestedNec || 0) + (vestedProfitSharing || 0)
  const knownUnvestedEmployerTotal = (matchVesting.unvested || 0)
    + (necVesting.unvested || 0)
    + (profitSharingVesting.unvested || 0)
  const vestingUnresolvedAmount = matchVesting.unresolved
    + necVesting.unresolved
    + profitSharingVesting.unresolved
  const vestingUnresolvedSources = [
    matchVesting.unresolved > 0 ? 'Match' : null,
    necVesting.unresolved > 0 ? 'NEC' : null,
    profitSharingVesting.unresolved > 0 ? 'Profit sharing' : null,
  ].filter(Boolean)

  // Forfeiture includes only amounts with a known unvested result.
  const estimatedForfeiture = isTerminated ? knownUnvestedEmployerTotal : 0
  const forfeitureUnresolvedAmount = isTerminated ? vestingUnresolvedAmount : 0

  // Cash-balance vesting runs on its own schedule and stays OUT of the DC vesting
  // and forfeiture totals so existing DC consumers remain byte-identical. Summary
  // surfaces can sum the DC and CB fields when it wants a combined figure.
  const cashBalanceVesting = getVestingAmounts(cashBalanceCredit, vestedCashBalancePercent)
  const vestedCashBalance = cashBalanceVesting.vested
  const unvestedCashBalance = cashBalanceVesting.unvested
  const cashBalanceVestingUnresolved = cashBalanceVesting.unresolved
  const cashBalanceForfeiture = isTerminated ? (cashBalanceVesting.unvested || 0) : 0

  // QNECs are new employer cost (unlike recycled forfeitures), so they join the
  // employer-cost totals; forfeitures do not.
  const dcEmployerCost = employerTotal + qnecAmount
  const combinedEmployerCost = cbEnabled ? null : employerTotal + qnecAmount

  // ==================== Final calculations ====================
  const totalContribution = totalEmployeeContribution + employerTotal + qnecAmount + afterTaxContribution
  // §415(c) annual additions include reallocated forfeitures and QNECs. Forfeitures
  // are recycled money (out of cost); QNECs are new employer cost (in cost above).
  const totalAdditions = regularTotal + employerTotal + afterTaxContribution + forfeitureAllocation + qnecAmount

  // Calculate maximum possible employer contribution (if employee deferred enough to max the match)
  let maxPossibleMatch = 0
  if (matchEnabled && allowsEmployerContributions) {
    if (matchMode !== 'grouped' || activeMatchGroup) {
      const maxMatchResult = calculateTieredMatch(100, cappedCompensation, activeMatchTiers)
      maxPossibleMatch = maxMatchResult.matchAmount
      if (matchCapPerEmployee > 0 && maxPossibleMatch > matchCapPerEmployee) {
        maxPossibleMatch = matchCapPerEmployee
      }
    }
  }
  const maxPossibleEmployerContribution = maxPossibleMatch + necAmount + profitSharingAmount

  // Participation status
  let participationStatus = 'non-participant'
  const hasDeferral = totalEmployeeContribution > 0

  if (hasDeferral) {
    const maxAllowed = regularDeferralCap + catchupLimit
    if (totalEmployeeContribution >= maxAllowed) {
      participationStatus = 'maxed'
    } else {
      participationStatus = 'participating'
    }
  }

  return {
    // Input data
    id: employee.id,
    name: employee.name || '',
    age,
    compensation,
    rawCompensation,
    compWasProrated,
    cappedCompensation,
    pretaxPercent: effectivePretaxPercent,
    rothPercent: effectiveRothPercent,
    totalDeferralPercent,
    usingPretaxAmount,
    usingRothAmount,

    // Employee contributions
    desiredDeferral,
    baseDeferral: regularTotal,
    actualPretax,
    actualRoth,
    catchupContribution,
    catchupType,
    catchupLimit,
    catchupEligible: catchupLimit > 0,
    totalEmployeeContribution,

    // After-tax (mega-backdoor Roth)
    afterTaxAllowed,
    afterTaxRequested: requestedAfterTax,
    afterTaxContribution,
    afterTaxCapacity,
    afterTaxLimitedBy415c,

    // Employer contributions - broken down by source
    matchAmount,
    matchMode,
    matchGroupLabel,
    matchGroupMatched,
    necAmount,
    // Preserve the contribution source for downstream gateway worksheets.
    // A safe-harbor NEC is a nonelective allocation; safe-harbor matching
    // remains match money and must not be relabeled as gateway allocation.
    necIsSafeHarbor: necEnabled && necIsSafeHarbor === true,
    profitSharingAmount,
    profitSharingGroupLabel,
    profitSharingGroupMatched,
    profitSharingLimitedBy415c,
    profitSharingComponents: profitSharingOverride?.components || [],
    ageWeightedProfitSharingAmount: Number(profitSharingOverride?.ageWeightedAmount) || 0,
    ageWeightedTestingAge: profitSharingOverride?.testingAge ?? null,
    ageWeightedFactor: profitSharingOverride?.factor ?? null,
    employerTotal,
    dcEmployerCost,
    matchBreakdown,

    // Cash balance (defined benefit) — §415(b), outside §415(c)
    cashBalanceEnabled: cbEnabled,
    cashBalanceCredit,
    cashBalanceGroupLabel,
    cashBalanceGroupMatched,
    combinedEmployerCost,

    // Vesting
    serviceYears,
    vestedMatchPercent,
    vestedNecPercent,
    vestedProfitSharingPercent,
    vestedMatch,
    vestedNec,
    vestedProfitSharing,
    knownVestedEmployerTotal,
    knownUnvestedEmployerTotal,
    vestingUnresolvedAmount,
    vestingUnresolvedSources,
    vestedEmployerTotal: knownVestedEmployerTotal,
    unvestedEmployerTotal: knownUnvestedEmployerTotal,
    estimatedForfeiture,
    forfeitureUnresolvedAmount,
    vestingEnabled,

    // Cash-balance vesting (parallel to DC, kept separate from DC totals)
    vestedCashBalancePercent,
    vestedCashBalance,
    unvestedCashBalance,
    cashBalanceVestingUnresolved,
    cashBalanceForfeiture,

    // Totals and limits
    totalContribution,
    totalAdditions,
    exceeds415c,
    limit415c: max415c,
    maxPossibleEmployerContribution,

    // Status flags
    isHCE,
    hceIsEstimated,
    isFivePercentOwner,
    ownershipPercent,
    attributedOwnershipFrom: attributedOwner ? String(employee.attributed_ownership_from) : null,
    gatewayCompensation,
    forfeitureAllocation,
    qnecAmount,
    profitSharingConditionMet,
    otherwiseExcludable,
    keyEmployeeStatus,
    allocationGroupLabel,
    participationStatus,
    eligibility: eligibilityResult,
    cashBalanceEligibility: cashBalanceEligibilityResult,

    // Employee data
    gender: employee.gender || null,
    hireDate: employee.hire_date || null,
    cashBalanceParticipantEntryDate: resolvedCbEntryDate ? formatDateOnly(resolvedCbEntryDate) : null,
    cashBalanceParticipantEntryDateSource: explicitCbEntryDate ? 'explicit' : 'unknown',
    cashBalanceOpeningBalance: Math.max(0, Number(employee.cb_opening_balance) || 0),
    cashBalancePriorAccruedMonthlyBenefit: Math.max(0, Number(employee.cb_prior_accrued_monthly_benefit) || 0),
    cashBalancePriorVestingYears: Math.max(0, Number(employee.cb_prior_vesting_years) || 0),
    priorAccruedBenefitMonthly: Math.max(0, Number(employee.cb_prior_accrued_monthly_benefit) || 0),
    priorVestingYears: Math.max(0, Number(employee.cb_prior_vesting_years) || 0),
    high3Compensation: resolvedHigh3,
    high3CompensationSource: Number.isFinite(explicitHigh3) ? 'explicit' : 'unknown',
    section415Compensation: resolved415Comp,
    section415CompensationSource: Number.isFinite(explicit415Comp) ? 'explicit' : 'unknown',
    terminationDate: employee.termination_date || null,
    effectiveTerminationDate: effectiveTerminationDate
      ? effectiveTerminationDate.toISOString().slice(0, 10)
      : null,
    hoursServed,
    tenure,
    fullTime: fullTimeFlag || null,
    balance: parseFloat(employee.balance) || 0,
    loanBalance: parseFloat(employee.loan_balance) || 0,
    status: employmentStatus,
    isTerminated,

    // Data quality
    warnings,
    reviewWarnings,
    advisoryWarnings,
  }
}

const profitSharingComponents = (plan) => (
  Array.isArray(plan?.employerContributions?.profitSharingAllocations)
    ? plan.employerContributions.profitSharingAllocations
    : []
)

// Whole-plan entry point and the only supported orchestration:
// compile → attribute → classify → resolve groups → calculate.
export function calculatePlanResults(employees = [], plan, { planKey = null, censusLocal = null } = {}) {
  // §318 family attribution is a cross-employee derivation, so it runs once
  // here rather than inside the per-employee calculation.
  const sourceEmployees = applyFamilyAttribution(Array.isArray(employees) ? employees : [])
  if (!plan) return { calculatedEmployees: [], metrics: null, chartData: null, allocationDiagnostics: [] }
  const authored = isAuthoredPlan(plan)
  if (!authored) {
    throw new Error('calculatePlanResults requires the current authored plan contract.')
  }
  const enginePlan = compileToEngine(plan)
  const allocationContext = { taxableWageBase: taxableWageBaseForYear(enginePlan.planYear) }
  const classifications = classifyEmployeesForPlan(sourceEmployees, enginePlan.planYear)
  const slotLocal = planKey ? censusLocal?.byPlanKey?.[planKey] ?? null : null
  // Class membership is the census row's own allocation_group code, so it
  // resolves straight off the source rows: no classification input, which is
  // what keeps a person's class from moving when their HCE status changes.
  allocationContext.resolvedGroupIds = resolveGroupMembership(sourceEmployees)
  allocationContext.groupLabelsById = Object.fromEntries(
    deriveAllocationClasses(sourceEmployees, referencedClassesForPlan(plan))
      .map((entry) => [entry.id, entry.id === UNCLASSED_CLASS_ID ? '' : entry.label]),
  )
  allocationContext.perPersonAmounts = slotLocal?.perPersonAmounts ?? {}
  const employeeContext = (employee, extra = {}) => {
    const context = {
      ...extra,
      classification: classifications.get(employee.id),
      groupLabelsById: allocationContext.groupLabelsById,
    }
    const resolved = allocationContext.resolvedGroupIds?.[employee.id]
    if (resolved) context.resolvedGroupIds = resolved
    return context
  }
  const components = profitSharingComponents(enginePlan)
  let calculatedEmployees
  let allocationDiagnostics = []
  if (!enginePlan.employerContributions?.profitSharingEnabled) {
    calculatedEmployees = sourceEmployees
      .map((employee) => calculateEmployeeContributions(employee, enginePlan.planYear, enginePlan, employeeContext(employee)))
      .filter(Boolean)
  } else {
    const basePlan = {
      ...enginePlan,
      employerContributions: { ...enginePlan.employerContributions, profitSharingEnabled: false },
    }
    const baseRows = sourceEmployees
      .map((employee) => calculateEmployeeContributions(employee, enginePlan.planYear, basePlan, employeeContext(employee)))
      .filter(Boolean)
    const baseById = new Map(baseRows.map((row) => [row.id, row]))
    const allocationResult = allocateProfitSharingComponents(
      baseRows,
      components,
      enginePlan.employerContributions.allocationLimitPolicy,
      allocationContext,
    )
    allocationDiagnostics = allocationResult.diagnostics
    calculatedEmployees = sourceEmployees.map((employee) => {
      const base = baseById.get(employee.id)
      const override = allocationResult.allocationsByEmployeeId[employee.id]
        || { amount: 0, ageWeightedAmount: 0, components: [] }
      if (!base?.eligibility?.allowsEmployerContributions) override.amount = 0
      return calculateEmployeeContributions(employee, enginePlan.planYear, enginePlan, employeeContext(employee, { profitSharingOverride: override }))
    }).filter(Boolean)
  }
  return {
    calculatedEmployees,
    metrics: sourceEmployees.length
      ? calculatePlanMetrics(calculatedEmployees, enginePlan.planYear, enginePlan.terminationHandling, enginePlan)
      : null,
    chartData: sourceEmployees.length ? getChartData(calculatedEmployees, enginePlan.terminationHandling) : null,
    allocationDiagnostics,
  }
}

// Permitted HCE-group average for Retira.app's modeled ADP/ACP screen. Delegates
// to the rigorous greater-of rule in ./compliance/adpAcp.js (the GREATER of
// 1.25x the NHCE average or the lesser of 2x / +2 points), per 26 CFR 1.401(k)-2
// and 1.401(m)-2. A plan administrator's formal test can still use different
// populations, compensation definitions, testing elections, corrections, and
// adjustments this screen does not model.
export function informalPercentageTestLimit(nhcePercentage) {
  return adpAcpLimit(nhcePercentage)
}

function averageContributionRate(employees, amountForEmployee) {
  const rates = employees
    .filter((employee) => Number(employee.cappedCompensation) > 0)
    .map((employee) => (Math.max(0, Number(amountForEmployee(employee)) || 0) / employee.cappedCompensation) * 100)
  return {
    average: rates.length ? rates.reduce((sum, rate) => sum + rate, 0) / rates.length : 0,
    count: rates.length,
  }
}

function informalTestResult(hceRate, nhceRate, hceCount, nhceCount) {
  const available = hceCount > 0 && nhceCount > 0
  const limit = available ? informalPercentageTestLimit(nhceRate) : null
  return {
    available,
    limit,
    margin: available ? limit - hceRate : null,
    status: !available ? 'unavailable' : hceRate <= limit ? 'within-basic-threshold' : 'over-basic-threshold',
  }
}

// Estimate the corrective distribution an over-the-limit ADP/ACP result would
// require, using the statutory PERCENTAGE-LEVELING method: the highest HCE
// contribution percentages are cut down to a common ceiling until the HCE average
// reaches the permitted limit. The aggregate dollars cut equals the excess
// contributions that would be refunded (then reallocated among HCEs by dollar
// leveling – which does not change the aggregate this estimates).
//
// `entries` is one row per HCE with usable compensation: { pct, comp }. This is a
// MODELED estimate only – it ignores investment earnings, the dollar-leveling
// allocation back to individuals, the QNEC alternative, gap-period income, and
// correction deadlines.
function estimateLevelingExcess(entries, targetAveragePercent) {
  const rows = entries.filter((row) => Number.isFinite(row.pct) && Number.isFinite(row.comp) && row.comp > 0)
  const n = rows.length
  if (!n) return { amount: 0, affectedCount: 0 }
  const targetSum = Math.max(0, targetAveragePercent) * n
  const currentSum = rows.reduce((sum, row) => sum + Math.max(0, row.pct), 0)
  if (currentSum <= targetSum) return { amount: 0, affectedCount: 0 }

  // Water-fill for the ceiling `cap` where Σ min(pct, cap) = targetSum. Every HCE
  // above the cap is leveled down to it; the area above the cap is the excess.
  const maxPct = rows.reduce((max, row) => Math.max(max, row.pct), 0)
  let lo = 0
  let hi = maxPct
  for (let iteration = 0; iteration < 100; iteration += 1) {
    const cap = (lo + hi) / 2
    const cappedSum = rows.reduce((sum, row) => sum + Math.min(Math.max(0, row.pct), cap), 0)
    if (cappedSum > targetSum) hi = cap
    else lo = cap
  }
  const cap = (lo + hi) / 2
  let amount = 0
  let affectedCount = 0
  for (const row of rows) {
    const over = Math.max(0, row.pct) - Math.min(Math.max(0, row.pct), cap)
    if (over > 1e-9) {
      amount += (over / 100) * row.comp
      affectedCount += 1
    }
  }
  return { amount, affectedCount }
}

export function calculatePlanMetrics(calculatedEmployees, planYear, terminationHandling = {}, rawPlanSettings = null) {
  const planSettings = isAuthoredPlan(rawPlanSettings) ? compileToEngine(rawPlanSettings) : rawPlanSettings
  if (planSettings && !Array.isArray(planSettings?.employerContributions?.profitSharingAllocations)) {
    throw new Error('calculatePlanMetrics requires an authored or compiled current plan.')
  }
  const totalEmployees = calculatedEmployees.length
  const cashBalanceEnabled = planSettings?.cashBalance?.enabled === true
    && Number(planYear) === CASH_BALANCE_SUPPORTED_PLAN_YEAR

  // Use centralized filtering for metrics
  const employeesForMetrics = getEmployeesForMetrics(calculatedEmployees, terminationHandling)

  const activeEmployees = calculatedEmployees.filter(isActiveEmployee)
  const terminatedEmployees = calculatedEmployees.filter(isTerminatedEmployee).length
  const dataQualityWarnings = calculatedEmployees
    .filter(e => e.reviewWarnings && e.reviewWarnings.length > 0)
    .length

  if (employeesForMetrics.length === 0) {
    return {
      totalEmployees,
      activeEmployees: activeEmployees.length,
      terminatedEmployees,
      employeesForMetrics: 0,
      hceCount: 0,
      nhceCount: 0,
      participationRate: 0,
      hceParticipationRate: 0,
      nhceParticipationRate: 0,
      avgDeferralPercent: 0,
      avgHCEDeferralPercent: 0,
      avgNHCEDeferralPercent: 0,
      avgEmployeeContribution: 0,
      avgEmployerContribution: 0,
      totalPretaxContributions: 0,
      totalRothContributions: 0,
      totalCatchupContributions: 0,
      totalEmployeeContributions: 0,
      totalEmployerMatch: 0,
      totalEmployerNec: 0,
      totalEmployerQnec: 0,
      totalEmployerProfitSharing: 0,
      totalEmployerContributions: 0,
      totalAfterTaxContributions: 0,
      totalContributions: 0,
      // Cash balance (defined benefit)
      cashBalanceEnabled,
      totalCashBalanceCredits: 0,
      totalCashBalanceHypotheticalCredits: 0,
      dcEmployerCost: 0,
      cashBalanceParticipantCount: 0,
      cashBalanceHceCredits: 0,
      cashBalanceNhceCredits: 0,
      cashBalanceKeyCredits: 0,
      cashBalanceHceCreditShare: 0,
      cashBalanceEligibleNhceCount: 0,
      cashBalanceEligibleNhceCreditCount: 0,
      cashBalanceHceOrKeyCreditCount: 0,
      cashBalanceAllocationStatus: 'none',
      minimumParticipationApplicable: cashBalanceEnabled,
      minimumParticipationAvailable: false,
      minimumParticipationUnavailableReason: cashBalanceEnabled ? 'no-participants' : null,
      minimumParticipationTestingAge: 65,
      minimumParticipationBenefitingCount: null,
      minimumParticipationRequiredCount: null,
      minimumParticipationNonExcludableCount: null,
      minimumParticipationMeaningfulRatePercent: null,
      minimumParticipationPasses: null,
      totalCombinedEmployerCost: cashBalanceEnabled ? null : 0,
      totalCashBalanceForfeitures: 0,
      totalVestedEmployer: 0,
      totalUnvestedEmployer: 0,
      totalVestingUnresolved: 0,
      vestingUnknownCount: 0,
      vestingUnknownEmployeeCount: 0,
      totalEstimatedForfeitures: 0,
      totalForfeitureUnresolved: 0,
      forfeitureUnknownCount: 0,
      forfeitureUnknownEmployeeCount: 0,
      eligibleEmployeeCount: 0,
      ineligibleEmployeeCount: 0,
      ltptEmployeeCount: 0,
      unknownEligibilityEmployeeCount: 0,
      avgCompensation: 0,
      totalCompensation: 0,
      maxedOutCount: 0,
      atAnnualAdditionsLimitCount: 0,
      catchupEligibleCount: 0,
      catchupUsedCount: 0,
      topHeavyKeyBalance: 0,
      topHeavyTotalBalance: 0,
      topHeavyRatio: null,
      isTopHeavy: false,
      topHeavyStatus: 'unavailable',
      topHeavyAvailable: false,
      topHeavyMissingKeyCount: 0,
      topHeavyUnavailableReason: 'no-employees',
      topHeavyMinPercent: 0,
      topHeavyMinBasis: null,
      topHeavyMinRequiredTotal: 0,
      topHeavyMinProvidedTotal: 0,
      topHeavyMinShortfall: 0,
      topHeavyMinShortfallCount: 0,
      topHeavyMinEligibleCount: 0,
      topHeavyMinSatisfiedBySafeHarbor: false,
      dataQualityWarnings,
      groupAllocationWarnings: [],
      hceEstimatedCount: 0,
      safeHarborType: 'none',
      isSafeHarbor: false,
      informalAdpAvailable: false,
      informalAdpHCE: 0,
      informalAdpNHCE: 0,
      informalAdpLimit: null,
      informalAdpMargin: null,
      informalAdpStatus: 'unavailable',
      informalAdpDeemedSatisfied: false,
      informalAdpCorrectionEstimate: 0,
      informalAdpCorrectionHceCount: 0,
      informalAcpAvailable: false,
      informalAcpHCE: 0,
      informalAcpNHCE: 0,
      informalAcpLimit: null,
      informalAcpMargin: null,
      informalAcpStatus: 'unavailable',
      informalAcpMatchDeemedSatisfied: false,
      informalAcpFullyDeemedSatisfied: false,
      informalAcpAfterTaxPresent: false,
      informalAcpCorrectionEstimate: 0,
      informalAcpCorrectionHceCount: 0,
      megaBackdoorAcp: {
        available: false,
        hceCount: 0,
        nhceCount: 0,
        nhceRate: 0,
        limit: null,
        safeHarborMatch: false,
        asEntered: { hceRate: 0, delta: 0, status: 'unavailable' },
        allHceMax: { hceRate: 0, delta: 0, status: 'unavailable' },
      },
      informalCoverageAvailable: false,
      informalCoverageHCEBenefitRate: 0,
      informalCoverageNHCEBenefitRate: 0,
      informalCoverageRatio: null,
      informalCoverageStatus: 'unavailable',
      informalCoverageUnavailableReason: 'insufficient-population',
      informalCoverageUnknownCount: 0,
      informalCoverageAvgBenefitHCE: 0,
      informalCoverageAvgBenefitNHCE: 0,
      informalCoverageAvgBenefitRatio: null,
      informalCoverageAvgBenefitMayPass: false,
      coverageClassificationStatus: 'unavailable',
      coverageConcentrationPercent: 0,
      coverageSafeHarborPercent: 50,
      coverageUnsafePercent: 40,
      coverageAbptRatio: null,
      coverageAbptStatus: 'unavailable',
      coverageAverageBenefitTestStatus: 'unavailable',
      coverageOverallStatus: 'unavailable',
      crossTestingApplicable: false,
      crossTesting: { available: false, status: 'unavailable', reason: 'no-employees' },
      crossTestingStatus: 'unavailable',
      crossTestingGatewayRate: null,
      crossTestingFailingRateGroupCount: 0,
    }
  }

  const hces = employeesForMetrics.filter(e => e.isHCE)
  const nhces = employeesForMetrics.filter(e => !e.isHCE)

  const participants = employeesForMetrics.filter(e => e.participationStatus !== 'non-participant')
  const hceParticipants = hces.filter(e => e.participationStatus !== 'non-participant')
  const nhceParticipants = nhces.filter(e => e.participationStatus !== 'non-participant')

  const maxedOut = employeesForMetrics.filter(e => e.participationStatus === 'maxed')
  // "Maxed out" above is the §402(g) employee elective-deferral limit. Separately
  // track who reached the §415(c) annual-additions cap (elective deferrals +
  // employer + after-tax), which is what moves when an owner is filled to the max
  // with employer or after-tax money rather than their own deferrals.
  const atAnnualAdditionsLimit = employeesForMetrics.filter(
    (e) => Number(e.limit415c) > 0 && Number(e.totalAdditions) >= Number(e.limit415c) - 0.005,
  )
  const catchupEligible = employeesForMetrics.filter(e => e.catchupEligible)
  const catchupUsed = employeesForMetrics.filter(e => e.catchupContribution > 0)

  const sum = (arr, key) => arr.reduce((s, e) => s + (Number.isFinite(e[key]) ? e[key] : 0), 0)
  const avg = (arr, key) => arr.length > 0 ? sum(arr, key) / arr.length : 0

  // Safe-harbor design deems the ADP test satisfied (and a safe-harbor MATCH also
  // deems the match portion of ACP satisfied). Detected from plan settings via the
  // shared module, so the screens and Julio's optimizer never disagree.
  const safeHarbor = evaluateSafeHarbor(planSettings?.employerContributions || {})

  const informalAdpHCE = averageContributionRate(hces, (employee) => employee.baseDeferral)
  const informalAdpNHCE = averageContributionRate(nhces, (employee) => employee.baseDeferral)
  const informalAdp = informalTestResult(
    informalAdpHCE.average,
    informalAdpNHCE.average,
    informalAdpHCE.count,
    informalAdpNHCE.count,
  )
  const informalAcpHCE = averageContributionRate(
    hces,
    (employee) => employee.matchAmount + employee.afterTaxContribution,
  )
  const informalAcpNHCE = averageContributionRate(
    nhces,
    (employee) => employee.matchAmount + employee.afterTaxContribution,
  )
  const informalAcp = informalTestResult(
    informalAcpHCE.average,
    informalAcpNHCE.average,
    informalAcpHCE.count,
    informalAcpNHCE.count,
  )
  // Employee after-tax (voluntary) contributions are ACP-tested even under safe
  // harbor, so a safe-harbor match only fully clears ACP when no after-tax money
  // is present.
  const informalAcpAfterTaxPresent = sum(employeesForMetrics, 'afterTaxContribution') > 0
  const informalAdpDeemedSatisfied = safeHarbor.deemsAdp
  const informalAcpMatchDeemedSatisfied = safeHarbor.deemsAcpMatch
  const informalAcpFullyDeemedSatisfied = safeHarbor.deemsAcpMatch && !informalAcpAfterTaxPresent

  // ===== Mega-backdoor Roth ACP estimator =====
  // After-tax contributions are ACP-tested (§401(m)) alongside match and are NOT
  // exempted by safe harbor. This projects the informal ACP screen for a "what if
  // every HCE maxes their after-tax to the plan cap" scenario (NHCEs held at their
  // entered elections – the worst case for the HCE/NHCE spread), so a firm can see
  // how much room a plan-document after-tax cap leaves before the spread blows up.
  // Directional only – never a pass/fail conclusion.
  const afterTaxAllowedForMetrics = employeesForMetrics.some((employee) => employee.afterTaxAllowed)
  const informalAcpProjectedHCE = averageContributionRate(
    hces,
    (employee) => employee.matchAmount + (Number(employee.afterTaxCapacity) || 0),
  )
  const informalAcpProjected = informalTestResult(
    informalAcpProjectedHCE.average,
    informalAcpNHCE.average,
    informalAcpProjectedHCE.count,
    informalAcpNHCE.count,
  )
  const megaBackdoorAcp = {
    // Meaningful only when the plan allows after-tax and both groups can be rated.
    available: afterTaxAllowedForMetrics && informalAcp.available,
    hceCount: informalAcpHCE.count,
    nhceCount: informalAcpNHCE.count,
    nhceRate: informalAcpNHCE.average,
    // The permitted HCE ceiling is derived from the NHCE average, which is the
    // same in both scenarios, so a single limit applies to both.
    limit: informalAcp.limit,
    safeHarborMatch: safeHarbor.deemsAcpMatch,
    asEntered: {
      hceRate: informalAcpHCE.average,
      delta: informalAcpHCE.average - informalAcpNHCE.average,
      status: informalAcp.status,
    },
    allHceMax: {
      hceRate: informalAcpProjectedHCE.average,
      delta: informalAcpProjectedHCE.average - informalAcpNHCE.average,
      status: informalAcpProjected.status,
    },
  }

  // Estimated corrective distribution if the screen is over its limit and is not
  // deemed satisfied by a safe harbor. Modeled with the percentage-leveling method.
  const adpCorrection = informalAdp.available && informalAdp.status === 'over-basic-threshold' && !informalAdpDeemedSatisfied
    ? estimateLevelingExcess(
        hces
          .filter((employee) => Number(employee.cappedCompensation) > 0)
          .map((employee) => ({
            pct: (Math.max(0, Number(employee.baseDeferral) || 0) / employee.cappedCompensation) * 100,
            comp: employee.cappedCompensation,
          })),
        informalAdp.limit,
      )
    : { amount: 0, affectedCount: 0 }
  // Only model an ACP correction when the match is NOT deemed satisfied. When a
  // safe-harbor match deems the match portion satisfied, only employee after-tax
  // money is ACP-tested – but the screen's limit is built on the match+after-tax
  // basis, so a combined-basis correction would overstate the after-tax-only gap.
  // Rather than emit a misleading figure, leave it at 0 and let the UI/Julio say
  // plainly that after-tax remains ACP-tested.
  const acpCorrection = informalAcp.available && informalAcp.status === 'over-basic-threshold' && !informalAcpMatchDeemedSatisfied
    ? estimateLevelingExcess(
        hces
          .filter((employee) => Number(employee.cappedCompensation) > 0)
          .map((employee) => ({
            pct: (Math.max(0, (Number(employee.matchAmount) || 0) + (Number(employee.afterTaxContribution) || 0)) / employee.cappedCompensation) * 100,
            comp: employee.cappedCompensation,
          })),
        informalAcp.limit,
      )
    : { amount: 0, affectedCount: 0 }

  // ==================== COVERAGE (§410(b)) ====================
  // Refined to the NONEXCLUDABLE population: employees who failed the plan's age/
  // service eligibility are statutorily excludable and don't belong in either the
  // numerator or the denominator. "Unknown" eligibility is retained (and counted
  // separately) rather than silently dropped.
  // LTPT employees may be excluded from coverage/nondiscrimination testing by
  // statute (SECURE 2.0), and this engine models plans as electing to do so.
  const isExcludable = (employee) => ['ineligible', 'ltpt'].includes(employee.eligibility?.status)
  const nonexcludableHces = hces.filter((employee) => !isExcludable(employee))
  const nonexcludableNhces = nhces.filter((employee) => !isExcludable(employee))
  const coverageUnknownCount = employeesForMetrics
    .filter((employee) => (employee.eligibility?.status ?? 'unknown') === 'unknown')
    .length
  const employerBenefitAmount = (employee) => (Number(employee.employerTotal) || 0)
    + (Number(employee.qnecAmount) || 0)
    + (Number(employee.forfeitureAllocation) || 0)
  const hceBenefiting = nonexcludableHces.filter((employee) => employerBenefitAmount(employee) > 0).length
  const nhceBenefiting = nonexcludableNhces.filter((employee) => employerBenefitAmount(employee) > 0).length
  // Average benefit percentages: employer-only (kept for the existing hint fields)
  // and a total-contributions basis (employer + elective deferrals + after-tax) for
  // the average-benefit-percentage prong of the average benefit test.
  const rateAvg = (group, amountFor) => {
    const rates = group
      .filter((employee) => Number(employee.cappedCompensation) > 0)
      .map((employee) => ((Math.max(0, Number(amountFor(employee)) || 0)) / employee.cappedCompensation) * 100)
    return rates.length ? rates.reduce((s, r) => s + r, 0) / rates.length : 0
  }
  const employerOnlyAmount = employerBenefitAmount
  const totalBenefitAmount = (e) => employerBenefitAmount(e)
    + Math.max(0, Number(e.baseDeferral) || 0)
    + (Number(e.afterTaxContribution) || 0)
  const informalCoverageAvgBenefitHCE = rateAvg(nonexcludableHces, employerOnlyAmount)
  const informalCoverageAvgBenefitNHCE = rateAvg(nonexcludableNhces, employerOnlyAmount)

  // The rigorous §410(b) evaluation: ratio-percentage test plus the average benefit
  // test (nondiscriminatory classification + ABPT). See ./compliance/coverage.js.
  const coverage = evaluateCoverage({
    hceBenefiting,
    nonexcludableHceCount: nonexcludableHces.length,
    nhceBenefiting,
    nonexcludableNhceCount: nonexcludableNhces.length,
    hceAvgBenefitPercent: rateAvg(nonexcludableHces, totalBenefitAmount),
    nhceAvgBenefitPercent: rateAvg(nonexcludableNhces, totalBenefitAmount),
  })

  // Product-facing coverage fields. The employer-benefit coverage screen only means something once the plan allocates
  // employer money; with zero employer contributions there is simply nothing to
  // test (unavailableReason 'no-employer-contributions').
  const informalCoverageHCEBenefitRate = coverage.hceBenefitingRate
  const informalCoverageNHCEBenefitRate = coverage.nhceBenefitingRate
  const informalCoverageAvailable = coverage.available
  const informalCoverageUnavailableReason = coverage.unavailableReason
  const informalCoverageRatio = coverage.ratioPercent
  const informalCoverageStatus = !coverage.available
    ? 'unavailable'
    : coverage.ratioStatus === 'passes-ratio' ? 'within-basic-threshold' : 'under-basic-threshold'
  const informalCoverageAvgBenefitRatio = informalCoverageAvgBenefitHCE > 0
    ? (informalCoverageAvgBenefitNHCE / informalCoverageAvgBenefitHCE) * 100
    : informalCoverageAvgBenefitNHCE > 0 ? 100 : null
  // Now backed by the real average benefit test (classification + ABPT) rather than
  // the benefit-percentage prong alone. Still never a pass on its own.
  const informalCoverageAvgBenefitMayPass = informalCoverageStatus === 'under-basic-threshold'
    && (coverage.status === 'passes-average-benefit' || coverage.status === 'may-pass-average-benefit')

  // ==================== CROSS-TESTING (§401(a)(4) general test) ====================
  // Tests the employer NONELECTIVE + profit-sharing allocation on a benefits basis
  // (EBARs), which is what a new-comparability / owner-heavy design relies on. Match
  // and elective deferrals are tested under ACP/ADP, not here. See
  // ./compliance/crossTesting.js. Meaningful only when such an allocation exists;
  // the cash-balance combined-plan gateway above is a separate, DB/DC screen.
  const crossTestAllocation = (employee) =>
    (Number(employee.necAmount) || 0) + (Number(employee.profitSharingAmount) || 0)
    + (Number(employee.forfeitureAllocation) || 0) + (Number(employee.qnecAmount) || 0)
  // §1.410(b)-5 average-benefit-percentage amount: every employer-provided
  // benefit — nonelective, profit sharing, match, AND elective deferrals —
  // but never catch-ups (baseDeferral already excludes them).
  const abptAllocation = (employee) => crossTestAllocation(employee)
    + (Number(employee.matchAmount) || 0) + (Number(employee.baseDeferral) || 0)
  const crossTestingApplicable = (nonexcludableHces.length + nonexcludableNhces.length) > 0
    && [...nonexcludableHces, ...nonexcludableNhces].some((e) => crossTestAllocation(e) > 0)
  const psComponents = planSettings?.employerContributions?.profitSharingAllocations || []
  const ageWeightedComponent = psComponents
    .find((component) => component.type === 'age_weighted')
  // §401(a)(4) testing-method elections (opt-in TPA levers). A pure age-weighted
  // design (a single uniform age_weighted component) inherently satisfies the
  // §1.401(a)(4)-8(b)(1) age/service-schedule route, so it is gateway-exempt
  // without the user asserting a route. Group-specific designs are not implied
  // by a generic age-weighted component.
  const ndt = planSettings?.nondiscriminationTesting || {}
  const pureAgeWeighted = psComponents.length === 1 && psComponents[0]?.type === 'age_weighted'
  const gatewayExempt = pureAgeWeighted || (ndt.gatewayRoute && ndt.gatewayRoute !== 'gateway')
  const otherwiseExcludableApplied = ndt.otherwiseExcludable === true
  const crossTesting = crossTestingApplicable
    ? evaluateCrossTesting([...nonexcludableHces, ...nonexcludableNhces].map((employee) => ({
        id: employee.id,
        isHCE: employee.isHCE,
        excludable: otherwiseExcludableApplied && employee.otherwiseExcludable === true,
        allocation: crossTestAllocation(employee),
        abptAllocation: abptAllocation(employee),
        comp: Number(employee.cappedCompensation) || 0,
        gatewayComp: Number(employee.gatewayCompensation) || undefined,
        age: employee.age,
        testingAge: employee.ageWeightedTestingAge ?? undefined,
        assumptionSet: ageWeightedComponent?.assumptionSet,
      })), {
        ...(ageWeightedComponent ? { assumptionSet: ageWeightedComponent.assumptionSet } : {}),
        gatewayExempt: gatewayExempt || undefined,
        gatewayRoute: ndt.gatewayRoute || 'gateway',
        otherwiseExcludableApplied,
        imputePermittedDisparity: ndt.imputePermittedDisparity === true,
        rateBanding: ndt.rateBanding === true,
        planYear,
      })
    : { available: false, status: 'unavailable', reason: 'no-nonelective-allocation' }

  // Terminated employees for forfeiture calculation
  const terminatedInCalculations = calculatedEmployees.filter(e => e.status === 'Terminated')

  // ==================== TOP-HEAVY ====================
  const keyEmployees = employeesForMetrics.filter(e => e.keyEmployeeStatus === 'key')
  const topHeavyMissingKeyCount = employeesForMetrics
    .filter(e => e.keyEmployeeStatus === 'unknown')
    .length
  const topHeavyKeyBalance = sum(keyEmployees, 'balance')
  const topHeavyTotalBalance = sum(employeesForMetrics, 'balance')
  const topHeavyAvailable = topHeavyMissingKeyCount === 0 && topHeavyTotalBalance > 0
  const topHeavyRatio = topHeavyAvailable
    ? (topHeavyKeyBalance / topHeavyTotalBalance) * 100
    : null
  const isTopHeavy = topHeavyRatio !== null && topHeavyRatio >= 60
  const topHeavyStatus = !topHeavyAvailable ? 'unavailable' : isTopHeavy ? 'top-heavy' : 'not-top-heavy'
  const topHeavyUnavailableReason = topHeavyMissingKeyCount > 0
    ? 'missing-key-employee-data'
    : topHeavyTotalBalance <= 0 ? 'no-balance-data' : null

  // Top-heavy minimum employer contribution. In a DC-only plan each non-key
  // participant generally must receive an employer contribution of at least the
  // lesser of 3% of compensation OR the highest contribution rate of any key
  // employee (the key's own elective deferrals count toward that rate).
  //
  // A cash-balance plan moves both halves of that rule. The "lesser of the
  // highest key rate" exception in §416(c)(2)(B) does not apply when the DC plan
  // sits in a required aggregation group that lets the DB plan satisfy
  // §401(a)(4) or §410(b), which is exactly what a combo design is. And for a
  // non-key participant covered under BOTH plans, the anti-duplication safe
  // harbor of §1.416-1 M-12 substitutes a 5% DC minimum for the DB accrual; a
  // participant in the DC plan alone stays at 3%.
  //
  // The other M-12 routes (a 2% DB minimum accrual, or proving comparability)
  // are often cheaper, but whether a given pay credit produces a 2% accrual of
  // high-3 average pay per year of service is an actuarial question this model
  // cannot answer, so they are disclosed rather than assumed. Cash-balance pay
  // credits never count toward the DC minimum either: crediting DB money
  // against it is the duplication M-12 exists to prevent.
  //
  // We estimate the requirement and the shortfall after crediting employer money
  // already allocated to each non-key. Limited to clearly-eligible employees;
  // unknown-eligibility records are excluded and flagged elsewhere.
  const keyContributionRate = (employee) => {
    const comp = Number(employee.cappedCompensation) || 0
    if (comp <= 0) return 0
    const employer = employerBenefitAmount(employee)
    const deferral = Math.max(0, Number(employee.baseDeferral) || 0)
    return ((employer + deferral) / comp) * 100
  }
  const maxKeyRate = keyEmployees.reduce((max, employee) => Math.max(max, keyContributionRate(employee)), 0)
  const topHeavyDbDcAggregated = isTopHeavy && cashBalanceEnabled
  const topHeavyMinPercentFor = (employee) => {
    if (!isTopHeavy) return 0
    if (!topHeavyDbDcAggregated) return Math.min(3, maxKeyRate)
    return (Number(employee.cashBalanceCredit) || 0) > 0 ? 5 : 3
  }
  const topHeavyNonKeyEligible = isTopHeavy
    ? employeesForMetrics.filter((e) => e.keyEmployeeStatus !== 'key' && e.eligibility?.status === 'eligible')
    : []
  // Headline rate for the screens: the highest per-person requirement in play.
  const topHeavyMinPercent = !isTopHeavy
    ? 0
    : topHeavyNonKeyEligible.length
      ? Math.max(...topHeavyNonKeyEligible.map(topHeavyMinPercentFor))
      : topHeavyDbDcAggregated ? 3 : Math.min(3, maxKeyRate)
  const topHeavyMinBasis = !isTopHeavy
    ? null
    : topHeavyDbDcAggregated ? 'db-dc-aggregation-m12' : 'dc-only'
  let topHeavyMinRequiredTotal = 0
  let topHeavyMinProvidedTotal = 0
  let topHeavyMinShortfall = 0
  let topHeavyMinShortfallCount = 0
  for (const employee of topHeavyNonKeyEligible) {
    const comp = Number(employee.cappedCompensation) || 0
    const required = (topHeavyMinPercentFor(employee) / 100) * comp
    const provided = employerBenefitAmount(employee)
    const shortfall = Math.max(0, required - provided)
    topHeavyMinRequiredTotal += required
    topHeavyMinProvidedTotal += provided
    if (shortfall > 0.005) {
      topHeavyMinShortfall += shortfall
      topHeavyMinShortfallCount += 1
    }
  }
  // A safe-harbor nonelective of at least the top-heavy minimum satisfies it for
  // everyone; surfaced so the UI/Julio can say so plainly.
  const topHeavyMinSatisfiedBySafeHarbor = isTopHeavy
    && safeHarbor.isNonelective
    && Number(planSettings?.employerContributions?.necPercent) >= topHeavyMinPercent

  // ==================== CASH BALANCE AGGREGATES ====================
  const totalCashBalanceCredits = sum(employeesForMetrics, 'cashBalanceCredit')
  const cashBalanceParticipantCount = employeesForMetrics
    .filter(e => (Number(e.cashBalanceCredit) || 0) > 0)
    .length
  const cashBalanceHceCredits = sum(hces, 'cashBalanceCredit')
  const cashBalanceNhceCredits = sum(nhces, 'cashBalanceCredit')
  const cashBalanceKeyCredits = sum(keyEmployees, 'cashBalanceCredit')
  const dcEmployerCost = sum(employeesForMetrics, 'dcEmployerCost')
  const totalCombinedEmployerCost = cashBalanceEnabled ? null : dcEmployerCost
  const totalCashBalanceForfeitures = sum(terminatedInCalculations, 'cashBalanceForfeiture')
  const cashBalanceCreditedEmployees = employeesForMetrics.filter(employee => (Number(employee.cashBalanceCredit) || 0) > 0)
  const cashBalanceHceOrKeyCreditCount = cashBalanceCreditedEmployees
    .filter(employee => employee.isHCE || employee.keyEmployeeStatus === 'key')
    .length
  const cashBalanceEligibleNhces = nhces
    .filter(employee => employee.eligibility?.status === 'eligible' && Number(employee.cappedCompensation) > 0)
  const cashBalanceEligibleNhceCreditCount = cashBalanceEligibleNhces
    .filter(employee => (Number(employee.cashBalanceCredit) || 0) > 0)
    .length
  const cashBalanceHceCreditShare = totalCashBalanceCredits > 0
    ? (cashBalanceHceCredits / totalCashBalanceCredits) * 100
    : 0
  const cashBalanceAllocationStatus = !cashBalanceEnabled || totalCashBalanceCredits <= 0
    ? 'none'
    : cashBalanceHceCredits > 0 && cashBalanceEligibleNhces.length > 0 && cashBalanceEligibleNhceCreditCount === 0
      ? 'hce-only'
      : cashBalanceHceCreditShare >= 80 && cashBalanceHceCredits > cashBalanceNhceCredits
        ? 'hce-concentrated'
        : 'mixed'

  // ==================== §401(a)(26) MINIMUM PARTICIPATION ====================
  //
  // A defined-benefit plan must benefit the lesser of 50 employees or the
  // greater of 40% of all employees or 2, counting only participants whose
  // accrual is MEANINGFUL. Counting anyone with a nonzero pay credit would
  // overstate the pass, so this runs the real benefit engine per person and
  // measures the year's accrual rate, exactly as the combined DB/DC testing
  // layer does (one shared helper, so the two cannot drift).
  //
  // Testing age is the modeling default of 65: the workspace has no adopted
  // normal retirement age to read, and inventing one per person would change
  // the answer silently. Disclosed through minimumParticipationTestingAge.
  const MINIMUM_PARTICIPATION_TESTING_AGE = 65
  const cashBalanceInterestCreditRate = Number(planSettings?.cashBalance?.interestCreditRate)
  const minimumParticipationPopulation = cashBalanceEnabled
    ? employeesForMetrics.filter((employee) => employee.eligibility?.status !== 'ineligible'
      && employee.eligibility?.status !== 'ltpt'
      && Number(employee.cappedCompensation) > 0)
    : []
  const minimumParticipationAccrualRates = []
  let minimumParticipationUnavailableReason = null
  if (cashBalanceEnabled) {
    if (!Number.isFinite(cashBalanceInterestCreditRate) || cashBalanceInterestCreditRate < 0) {
      minimumParticipationUnavailableReason = 'missing-interest-credit'
    } else if (!minimumParticipationPopulation.length) {
      minimumParticipationUnavailableReason = 'no-participants'
    }
    for (const employee of minimumParticipationPopulation) {
      if (minimumParticipationUnavailableReason) break
      // Blank is not zero. Number(null) is 0, which the benefit engine would
      // accept as a newborn and compound for 65 years into an enormous accrual
      // rate, silently passing the screen. A missing age makes the whole screen
      // unavailable instead.
      if (employee.age === null || employee.age === undefined || employee.age === ''
        || !Number.isFinite(Number(employee.age))) {
        minimumParticipationUnavailableReason = 'missing-age'
        break
      }
      const benefit = evaluateCashBalanceBenefit({
        payCredit: Number(employee.cashBalanceCredit) || 0,
        interestCredit: cashBalanceInterestCreditRate / 100,
        age: employee.age,
        testingAge: MINIMUM_PARTICIPATION_TESTING_AGE,
        comp: Number(employee.cappedCompensation) || 0,
      })
      if (benefit.status !== 'ok') {
        // A missing age or an age outside the anchored APR span makes the whole
        // screen unavailable rather than quietly counting that person as zero.
        minimumParticipationUnavailableReason = benefit.reason || 'unavailable'
        break
      }
      minimumParticipationAccrualRates.push(benefit.accrualRatePercent)
    }
  }
  const minimumParticipationAvailable = cashBalanceEnabled && !minimumParticipationUnavailableReason
  const minimumParticipation = minimumParticipationAvailable
    ? evaluateMinimumParticipation(minimumParticipationAccrualRates)
    : null

  // Data quality
  const hceEstimatedCount = employeesForMetrics.filter(e => e.hceIsEstimated).length
  const vestingUnknownCount = employeesForMetrics
    .filter(e => e.vestingUnresolvedAmount > 0)
    .length
  const forfeitureUnknownCount = terminatedInCalculations
    .filter(e => e.forfeitureUnresolvedAmount > 0)
    .length
  const groupAllocationWarnings = []
  const countLabel = (count, singular, plural = `${singular}s`) => `${count} ${count === 1 ? singular : plural}`
  if (cashBalanceEnabled) {
    if (cashBalanceAllocationStatus === 'hce-only') {
      groupAllocationWarnings.push(`Cash balance credits are going to ${countLabel(cashBalanceHceOrKeyCreditCount, 'HCE/key employee')} and 0 eligible NHCEs. Retira flags this as a potential coverage/nondiscrimination concern for professional review; consider an NHCE/catch-all credit and confirm the design with the plan's TPA or actuary.`)
    } else if (cashBalanceAllocationStatus === 'hce-concentrated') {
      groupAllocationWarnings.push(`Cash balance credits are highly concentrated in HCEs (${Math.round(cashBalanceHceCreditShare)}% of cash-balance dollars). Retira.app does not run formal defined-benefit nondiscrimination testing; confirm coverage, gateway, and cross-testing with the plan's TPA or actuary.`)
    }

    groupAllocationWarnings.push('Cash balance credits are planning estimates — confirm actual credits with the plan\'s TPA or actuary.')
  }

  return {
    totalEmployees,
    activeEmployees: activeEmployees.length,
    terminatedEmployees,
    employeesForMetrics: employeesForMetrics.length,
    hceCount: hces.length,
    nhceCount: nhces.length,

    participationRate: (participants.length / employeesForMetrics.length) * 100,
    hceParticipationRate: hces.length > 0 ? (hceParticipants.length / hces.length) * 100 : 0,
    nhceParticipationRate: nhces.length > 0 ? (nhceParticipants.length / nhces.length) * 100 : 0,

    avgDeferralPercent: avg(employeesForMetrics, 'totalDeferralPercent'),
    avgHCEDeferralPercent: avg(hces, 'totalDeferralPercent'),
    avgNHCEDeferralPercent: avg(nhces, 'totalDeferralPercent'),

    avgEmployeeContribution: avg(employeesForMetrics, 'totalEmployeeContribution'),
    avgEmployerContribution: avg(employeesForMetrics, 'dcEmployerCost'),

    totalPretaxContributions: sum(employeesForMetrics, 'actualPretax'),
    totalRothContributions: sum(employeesForMetrics, 'actualRoth'),
    totalCatchupContributions: sum(employeesForMetrics, 'catchupContribution'),
    totalEmployeeContributions: sum(employeesForMetrics, 'totalEmployeeContribution'),

    // Employer contribution breakdown
    totalEmployerMatch: sum(employeesForMetrics, 'matchAmount'),
    totalEmployerNec: sum(employeesForMetrics, 'necAmount'),
    totalEmployerQnec: sum(employeesForMetrics, 'qnecAmount'),
    totalEmployerProfitSharing: sum(employeesForMetrics, 'profitSharingAmount'),
    totalEmployerContributions: sum(employeesForMetrics, 'dcEmployerCost'),

    totalAfterTaxContributions: sum(employeesForMetrics, 'afterTaxContribution'),

    totalContributions: sum(employeesForMetrics, 'totalContribution'),

    // Cash balance (defined benefit) aggregates
    cashBalanceEnabled,
    totalCashBalanceCredits,
    totalCashBalanceHypotheticalCredits: totalCashBalanceCredits,
    dcEmployerCost,
    cashBalanceParticipantCount,
    cashBalanceHceCredits,
    cashBalanceNhceCredits,
    cashBalanceKeyCredits,
    cashBalanceHceCreditShare,
    cashBalanceEligibleNhceCount: cashBalanceEligibleNhces.length,
    cashBalanceEligibleNhceCreditCount,
    cashBalanceHceOrKeyCreditCount,
    cashBalanceAllocationStatus,
    // §401(a)(26) minimum participation (cash balance only).
    minimumParticipationApplicable: cashBalanceEnabled,
    minimumParticipationAvailable,
    minimumParticipationUnavailableReason,
    minimumParticipationTestingAge: MINIMUM_PARTICIPATION_TESTING_AGE,
    minimumParticipationBenefitingCount: minimumParticipation?.benefitingCount ?? null,
    minimumParticipationRequiredCount: minimumParticipation?.requiredBenefiting ?? null,
    minimumParticipationNonExcludableCount: minimumParticipation?.nonExcludableCount ?? null,
    minimumParticipationMeaningfulRatePercent: minimumParticipation?.meaningfulAccrualRatePercent ?? null,
    minimumParticipationPasses: minimumParticipation?.passes ?? null,
    totalCombinedEmployerCost,
    totalCashBalanceForfeitures,
    // Vesting totals
    totalVestedEmployer: sum(employeesForMetrics, 'vestedEmployerTotal'),
    totalUnvestedEmployer: sum(employeesForMetrics, 'unvestedEmployerTotal'),
    totalVestingUnresolved: sum(employeesForMetrics, 'vestingUnresolvedAmount'),
    vestingUnknownCount,
    vestingUnknownEmployeeCount: vestingUnknownCount,
    totalEstimatedForfeitures: sum(terminatedInCalculations, 'estimatedForfeiture'),
    totalForfeitureUnresolved: sum(terminatedInCalculations, 'forfeitureUnresolvedAmount'),
    forfeitureUnknownCount,
    forfeitureUnknownEmployeeCount: forfeitureUnknownCount,

    eligibleEmployeeCount: employeesForMetrics.filter(e => e.eligibility.status === 'eligible').length,
    ineligibleEmployeeCount: employeesForMetrics.filter(e => e.eligibility.status === 'ineligible').length,
    ltptEmployeeCount: employeesForMetrics.filter(e => e.eligibility.status === 'ltpt').length,
    unknownEligibilityEmployeeCount: employeesForMetrics.filter(e => e.eligibility.status === 'unknown').length,

    avgCompensation: avg(employeesForMetrics, 'compensation'),
    totalCompensation: sum(employeesForMetrics, 'compensation'),

    maxedOutCount: maxedOut.length,
    atAnnualAdditionsLimitCount: atAnnualAdditionsLimit.length,
    catchupEligibleCount: catchupEligible.length,
    catchupUsedCount: catchupUsed.length,

    // By gender
    maleCount: employeesForMetrics.filter(e => e.gender === 'M').length,
    femaleCount: employeesForMetrics.filter(e => e.gender === 'F').length,
    maleAvgDeferral: avg(employeesForMetrics.filter(e => e.gender === 'M'), 'totalDeferralPercent'),
    femaleAvgDeferral: avg(employeesForMetrics.filter(e => e.gender === 'F'), 'totalDeferralPercent'),

    // Simplified deferral comparison (HCE/NHCE)
    adpHCE: avg(hces, 'totalDeferralPercent'),
    adpNHCE: avg(nhces, 'totalDeferralPercent'),
    adpSpread: avg(hces, 'totalDeferralPercent') - avg(nhces, 'totalDeferralPercent'),

    // Informal compliance screens. These intentionally expose assumptions and
    // never represent a formal plan-administrator test result.
    informalAdpAvailable: informalAdp.available,
    informalAdpHCE: informalAdpHCE.average,
    informalAdpNHCE: informalAdpNHCE.average,
    informalAdpLimit: informalAdp.limit,
    informalAdpMargin: informalAdp.margin,
    informalAdpStatus: informalAdp.status,
    informalAdpDeemedSatisfied,
    informalAdpCorrectionEstimate: adpCorrection.amount,
    informalAdpCorrectionHceCount: adpCorrection.affectedCount,
    informalAcpAvailable: informalAcp.available,
    informalAcpHCE: informalAcpHCE.average,
    informalAcpNHCE: informalAcpNHCE.average,
    informalAcpLimit: informalAcp.limit,
    informalAcpMargin: informalAcp.margin,
    informalAcpStatus: informalAcp.status,
    informalAcpMatchDeemedSatisfied,
    informalAcpFullyDeemedSatisfied,
    informalAcpAfterTaxPresent,
    informalAcpCorrectionEstimate: acpCorrection.amount,
    informalAcpCorrectionHceCount: acpCorrection.affectedCount,
    megaBackdoorAcp,
    informalCoverageAvailable,
    informalCoverageHCEBenefitRate,
    informalCoverageNHCEBenefitRate,
    informalCoverageRatio,
    informalCoverageStatus,
    informalCoverageUnavailableReason: informalCoverageUnavailableReason,
    informalCoverageUnknownCount: coverageUnknownCount,
    informalCoverageAvgBenefitHCE,
    informalCoverageAvgBenefitNHCE,
    informalCoverageAvgBenefitRatio,
    informalCoverageAvgBenefitMayPass,
    // Rigorous §410(b) detail (ratio + average benefit test): classification and
    // ABPT prongs, and the overall determination. Modeled, not a formal test.
    coverageClassificationStatus: coverage.classificationStatus,
    coverageConcentrationPercent: coverage.concentrationPercent,
    coverageSafeHarborPercent: coverage.safeHarborPercent,
    coverageUnsafePercent: coverage.unsafePercent,
    coverageAbptRatio: coverage.abptRatio,
    coverageAbptStatus: coverage.abptStatus,
    coverageAverageBenefitTestStatus: coverage.averageBenefitTestStatus,
    coverageOverallStatus: coverage.status,
    // Modeled §401(a)(4) general test (cross-testing) for the nonelective/PS
    // allocation. Not a formal test; see ./compliance/crossTesting.js.
    crossTestingApplicable,
    crossTesting,
    crossTestingStatus: crossTesting.status,
    crossTestingGatewayRate: crossTesting.gatewayRate ?? null,
    crossTestingFailingRateGroupCount: crossTesting.failingRateGroupCount ?? 0,

    // Safe-harbor design (modeled from plan settings).
    safeHarborType: safeHarbor.type,
    isSafeHarbor: safeHarbor.deemsAdp,

    // Maximum employer contribution (if every eligible employee maxes out the match)
    estimatedMaxEmployerContribution: sum(employeesForMetrics, 'maxPossibleEmployerContribution'),

    // Top Heavy
    topHeavyKeyBalance,
    topHeavyTotalBalance,
    topHeavyRatio,
    isTopHeavy,
    topHeavyStatus,
    topHeavyAvailable,
    topHeavyMissingKeyCount,
    topHeavyUnavailableReason,
    // Top-heavy minimum employer contribution (only meaningful when top-heavy).
    topHeavyMinPercent,
    topHeavyMinBasis,
    topHeavyMinRequiredTotal,
    topHeavyMinProvidedTotal,
    topHeavyMinShortfall,
    topHeavyMinShortfallCount,
    topHeavyMinEligibleCount: topHeavyNonKeyEligible.length,
    topHeavyMinSatisfiedBySafeHarbor,

    // Data quality
    dataQualityWarnings,
    groupAllocationWarnings,
    hceEstimatedCount,
  }
}

export function getChartData(calculatedEmployees, terminationHandling = {}) {
  // Use centralized filtering for chart data
  const employeesForMetrics = getEmployeesForMetrics(calculatedEmployees, terminationHandling)
  const employeeIds = (employees) => employees.map(e => e.id)

  // Deferral distribution histogram
  const deferralBuckets = [
    { range: '0%', min: 0, max: 0 },
    { range: '1-3%', min: 0.01, max: 3 },
    { range: '4-6%', min: 3.01, max: 6 },
    { range: '7-10%', min: 6.01, max: 10 },
    { range: '11-15%', min: 10.01, max: 15 },
    { range: '>15%', min: 15.01, max: 100 },
  ]

  const deferralDistribution = deferralBuckets.map(bucket => {
    const bucketEmployees = employeesForMetrics.filter(e =>
      e.totalDeferralPercent >= bucket.min && e.totalDeferralPercent <= bucket.max
    )
    return {
      range: bucket.range,
      count: bucketEmployees.length,
      employeeIds: employeeIds(bucketEmployees),
    }
  })

  // Compensation distribution
  const compBuckets = [
    { range: '<$50K', min: 0, max: 50000 },
    { range: '$50-75K', min: 50000, max: 75000 },
    { range: '$75-100K', min: 75000, max: 100000 },
    { range: '$100-150K', min: 100000, max: 150000 },
    { range: '$150-200K', min: 150000, max: 200000 },
    { range: '>$200K', min: 200000, max: Infinity },
  ]

  const compensationDistribution = compBuckets.map(bucket => {
    const bucketEmployees = employeesForMetrics.filter(e =>
      e.compensation >= bucket.min && e.compensation < bucket.max
    )
    return {
      range: bucket.range,
      count: bucketEmployees.length,
      employeeIds: employeeIds(bucketEmployees),
    }
  })

  // Age band contributions
  const ageBands = ['Under 25', '25-34', '35-44', '45-54', '55-64', '65+']
  const getAgeBand = (age) => {
    if (age === null) return null
    if (age < 25) return 'Under 25'
    if (age < 35) return '25-34'
    if (age < 45) return '35-44'
    if (age < 55) return '45-54'
    if (age < 65) return '55-64'
    return '65+'
  }

  const ageContributions = ageBands.map(band => {
    const bandEmployees = employeesForMetrics.filter(e => getAgeBand(e.age) === band)
    return {
      ageBand: band,
      count: bandEmployees.length,
      employeeIds: employeeIds(bandEmployees),
      avgPretax: bandEmployees.length > 0
        ? bandEmployees.reduce((s, e) => s + e.actualPretax, 0) / bandEmployees.length
        : 0,
      avgRoth: bandEmployees.length > 0
        ? bandEmployees.reduce((s, e) => s + e.actualRoth, 0) / bandEmployees.length
        : 0,
      avgCatchup: bandEmployees.length > 0
        ? bandEmployees.reduce((s, e) => s + e.catchupContribution, 0) / bandEmployees.length
        : 0,
      avgEmployer: bandEmployees.length > 0
        ? bandEmployees.reduce((s, e) => s + e.dcEmployerCost, 0) / bandEmployees.length
        : 0,
      avgAfterTax: bandEmployees.length > 0
        ? bandEmployees.reduce((s, e) => s + (e.afterTaxContribution || 0), 0) / bandEmployees.length
        : 0,
      avgDeferralPercent: bandEmployees.length > 0
        ? bandEmployees.reduce((s, e) => s + e.totalDeferralPercent, 0) / bandEmployees.length
        : 0,
    }
  })

  // Participation status pie
  const nonParticipants = employeesForMetrics.filter(e => e.participationStatus === 'non-participant')
  const participants = employeesForMetrics.filter(e => e.participationStatus === 'participating')
  const maxedOut = employeesForMetrics.filter(e => e.participationStatus === 'maxed')

  const participationStatus = [
    { name: 'Non-participant', value: nonParticipants.length, employeeIds: employeeIds(nonParticipants) },
    { name: 'Participating', value: participants.length, employeeIds: employeeIds(participants) },
    { name: 'Maxed out', value: maxedOut.length, employeeIds: employeeIds(maxedOut) },
  ].filter(d => d.value > 0)

  // Catch-up usage pie
  const notCatchupEligible = employeesForMetrics.filter(e => !e.catchupEligible)
  const catchupEligibleNotUsed = employeesForMetrics.filter(e => e.catchupEligible && e.catchupContribution === 0)
  const usingCatchup = employeesForMetrics.filter(e => e.catchupContribution > 0)

  const catchupUsage = [
    { name: 'Not eligible', value: notCatchupEligible.length, employeeIds: employeeIds(notCatchupEligible) },
    { name: 'Eligible, not used', value: catchupEligibleNotUsed.length, employeeIds: employeeIds(catchupEligibleNotUsed) },
    { name: 'Using catch-up', value: usingCatchup.length, employeeIds: employeeIds(usingCatchup) },
  ].filter(d => d.value > 0)

  // HCE vs NHCE comparison
  const hces = employeesForMetrics.filter(e => e.isHCE)
  const nhces = employeesForMetrics.filter(e => !e.isHCE)

  const hceNhceComparison = [
    {
      category: 'Avg Deferral %',
      HCE: hces.length > 0 ? hces.reduce((s, e) => s + e.totalDeferralPercent, 0) / hces.length : 0,
      NHCE: nhces.length > 0 ? nhces.reduce((s, e) => s + e.totalDeferralPercent, 0) / nhces.length : 0,
      hceEmployeeIds: employeeIds(hces),
      nhceEmployeeIds: employeeIds(nhces),
    },
    {
      category: 'Avg Contribution ($K)',
      HCE: hces.length > 0 ? (hces.reduce((s, e) => s + e.totalEmployeeContribution, 0) / hces.length) / 1000 : 0,
      NHCE: nhces.length > 0 ? (nhces.reduce((s, e) => s + e.totalEmployeeContribution, 0) / nhces.length) / 1000 : 0,
      hceEmployeeIds: employeeIds(hces),
      nhceEmployeeIds: employeeIds(nhces),
    },
    {
      category: 'Participation %',
      HCE: hces.length > 0 ? (hces.filter(e => e.participationStatus !== 'non-participant').length / hces.length) * 100 : 0,
      NHCE: nhces.length > 0 ? (nhces.filter(e => e.participationStatus !== 'non-participant').length / nhces.length) * 100 : 0,
      hceEmployeeIds: employeeIds(hces),
      nhceEmployeeIds: employeeIds(nhces),
    },
  ]

  // Scatter plot data: Compensation vs Deferral %
  const compVsDeferral = employeesForMetrics.map(e => ({
    id: e.id,
    name: e.name,
    compensation: e.compensation,
    deferralPercent: e.totalDeferralPercent,
    isHCE: e.isHCE,
    age: e.age,
    ageBand: getAgeBand(e.age),
    employeeIds: [e.id],
  }))

  // Scatter plot data: Age vs Contribution
  const ageVsContribution = employeesForMetrics.filter(e => e.age !== null).map(e => ({
    id: e.id,
    name: e.name,
    age: e.age,
    contribution: e.totalEmployeeContribution,
    catchupEligible: e.catchupEligible,
    employeeIds: [e.id],
  }))

  // Employer contribution breakdown by age band
  const employerByAgeBand = ageBands.map(band => {
    const bandEmployees = employeesForMetrics.filter(e => getAgeBand(e.age) === band)
    return {
      ageBand: band,
      count: bandEmployees.length,
      employeeIds: employeeIds(bandEmployees),
      totalEmployer: bandEmployees.reduce((s, e) => s + e.dcEmployerCost, 0),
      avgEmployer: bandEmployees.length > 0
        ? bandEmployees.reduce((s, e) => s + e.dcEmployerCost, 0) / bandEmployees.length
        : 0,
      avgMatch: bandEmployees.length > 0
        ? bandEmployees.reduce((s, e) => s + e.matchAmount, 0) / bandEmployees.length
        : 0,
      avgNec: bandEmployees.length > 0
        ? bandEmployees.reduce((s, e) => s + e.necAmount, 0) / bandEmployees.length
        : 0,
      avgQnec: bandEmployees.length > 0
        ? bandEmployees.reduce((s, e) => s + e.qnecAmount, 0) / bandEmployees.length
        : 0,
      avgProfitSharing: bandEmployees.length > 0
        ? bandEmployees.reduce((s, e) => s + e.profitSharingAmount, 0) / bandEmployees.length
        : 0,
      // Cash-balance pay credit (defined benefit), stacked separately from DC.
      avgCashBalance: bandEmployees.length > 0
        ? bandEmployees.reduce((s, e) => s + (e.cashBalanceCredit || 0), 0) / bandEmployees.length
        : 0,
    }
  })

  // ==================== CENSUS DEMOGRAPHICS (no financials) ====================

  // Age Distribution - employee count by age band
  const ageDistribution = ageBands.map(band => {
    const bandEmployees = employeesForMetrics.filter(e => getAgeBand(e.age) === band)
    return {
      ageBand: band,
      count: bandEmployees.length,
      employeeIds: employeeIds(bandEmployees),
    }
  })

  // Gender Distribution
  const maleCount = employeesForMetrics.filter(e => e.gender === 'M').length
  const femaleCount = employeesForMetrics.filter(e => e.gender === 'F').length
  const notSpecifiedCount = employeesForMetrics.filter(e => e.gender !== 'M' && e.gender !== 'F').length
  const totalForGender = employeesForMetrics.length

  const genderDistribution = [
    {
      name: 'Male',
      value: maleCount,
      pct: totalForGender > 0 ? (maleCount / totalForGender) * 100 : 0,
      employeeIds: employeeIds(employeesForMetrics.filter(e => e.gender === 'M')),
    },
    {
      name: 'Female',
      value: femaleCount,
      pct: totalForGender > 0 ? (femaleCount / totalForGender) * 100 : 0,
      employeeIds: employeeIds(employeesForMetrics.filter(e => e.gender === 'F')),
    },
    {
      name: 'Not specified',
      value: notSpecifiedCount,
      pct: totalForGender > 0 ? (notSpecifiedCount / totalForGender) * 100 : 0,
      employeeIds: employeeIds(employeesForMetrics.filter(e => e.gender !== 'M' && e.gender !== 'F')),
    },
  ]

  // Tenure Distribution
  const tenureBands = [
    { range: '0-1yr', minMonths: 0, maxMonths: 12 },
    { range: '1-3yr', minMonths: 12, maxMonths: 36 },
    { range: '3-5yr', minMonths: 36, maxMonths: 60 },
    { range: '5-10yr', minMonths: 60, maxMonths: 120 },
    { range: '10-20yr', minMonths: 120, maxMonths: 240 },
    { range: '20yr+', minMonths: 240, maxMonths: Infinity },
  ]

  const tenureDistribution = tenureBands.map(bucket => {
    const bucketEmployees = employeesForMetrics.filter(e => {
      const totalMonths = e.tenure?.totalMonths
      if (totalMonths === null || totalMonths === undefined) return false
      return totalMonths >= bucket.minMonths && totalMonths < bucket.maxMonths
    })
    const maleEmps = bucketEmployees.filter(e => e.gender === 'M')
    const femaleEmps = bucketEmployees.filter(e => e.gender === 'F')
    const undisclosedEmps = bucketEmployees.filter(e => e.gender !== 'M' && e.gender !== 'F')
    return {
      range: bucket.range,
      count: bucketEmployees.length,
      male: maleEmps.length,
      female: femaleEmps.length,
      undisclosed: undisclosedEmps.length,
      maleIds: employeeIds(maleEmps),
      femaleIds: employeeIds(femaleEmps),
      undisclosedIds: employeeIds(undisclosedEmps),
      employeeIds: employeeIds(bucketEmployees),
    }
  })
  const unknownTenureEmployees = employeesForMetrics.filter(e => !e.tenure)
  tenureDistribution.push({
    range: 'Unknown',
    count: unknownTenureEmployees.length,
    male: unknownTenureEmployees.filter(e => e.gender === 'M').length,
    female: unknownTenureEmployees.filter(e => e.gender === 'F').length,
    undisclosed: unknownTenureEmployees.filter(e => e.gender !== 'M' && e.gender !== 'F').length,
    maleIds: employeeIds(unknownTenureEmployees.filter(e => e.gender === 'M')),
    femaleIds: employeeIds(unknownTenureEmployees.filter(e => e.gender === 'F')),
    undisclosedIds: employeeIds(unknownTenureEmployees.filter(e => e.gender !== 'M' && e.gender !== 'F')),
    employeeIds: employeeIds(unknownTenureEmployees),
  })

  // ==================== LOAN CHARTS ====================

  const employeesWithLoans = employeesForMetrics.filter(e => e.loanBalance > 0)

  const loanByAgeBand = ageBands.map(band => {
    const bandEmployees = employeesWithLoans.filter(e => getAgeBand(e.age) === band)
    const bandTotal = employeesForMetrics.filter(e => getAgeBand(e.age) === band).length
    return {
      ageBand: band,
      count: bandEmployees.length,
      bandTotal,
      usageRate: bandTotal > 0 ? (bandEmployees.length / bandTotal) * 100 : 0,
      totalLoan: bandEmployees.reduce((s, e) => s + e.loanBalance, 0),
      avgLoan: bandEmployees.length > 0
        ? bandEmployees.reduce((s, e) => s + e.loanBalance, 0) / bandEmployees.length
        : 0,
      employeeIds: employeeIds(bandEmployees),
    }
  })

  const loanVsBalance = employeesWithLoans.map(e => ({
    id: e.id,
    name: e.name,
    balance: e.balance,
    loanBalance: e.loanBalance,
    age: e.age,
    isHCE: e.isHCE,
    loanToBalanceRatio: e.balance > 0 ? e.loanBalance / e.balance : 0,
    employeeIds: [e.id],
  }))

  const loanBuckets = [
    { range: '$1-5K', min: 1, max: 5000 },
    { range: '$5-10K', min: 5000, max: 10000 },
    { range: '$10-25K', min: 10000, max: 25000 },
    { range: '$25-50K', min: 25000, max: 50000 },
    { range: '$50K+', min: 50000, max: Infinity },
  ]

  const loanDistribution = loanBuckets.map(bucket => {
    const bucketEmployees = employeesWithLoans.filter(e =>
      e.loanBalance >= bucket.min && e.loanBalance < bucket.max
    )
    return {
      range: bucket.range,
      count: bucketEmployees.length,
      employeeIds: employeeIds(bucketEmployees),
    }
  })

  return {
    deferralDistribution,
    compensationDistribution,
    ageContributions,
    participationStatus,
    catchupUsage,
    hceNhceComparison,
    compVsDeferral,
    ageVsContribution,
    employerByAgeBand,
    // Census demographics (no financials)
    ageDistribution,
    genderDistribution,
    tenureDistribution,
    // Loan charts
    loanByAgeBand,
    loanVsBalance,
    loanDistribution,
  }
}
